import type { ProofDateRange } from '@/types'
import { authenticationGeneration, protectedActionsPaused, reportAuthenticationFailure } from './authLifecycle'
/**
 * SalesRep API client
 *
 * All endpoints are under the /sr route prefix on the AccountScout API.
 * Backend properties are PascalCase; this layer maps them to camelCase.
 */

import axios, { type AxiosInstance } from 'axios'
import type { AccountSnapshot, SnapshotRunDetails, SnapshotArtwork, SnapshotSelection } from '@/types/snapshot'
import { readPaymentSafety } from '@/utils/paymentSafety'
import { getApiBaseUrl, getApiKey, getTenantId, getSessionToken } from './config'
import type {
  LoginRequest,
  LoginResponse,
  VerifyCodeRequest,
  VerifyCodeResponse,
  SalesRepInfo,
  ProofInfo,
  ProofDetailResponse,
  ProofListResponse,
  ProofSummaryResponse,
  ApproveProofResponse,
  RequestChangesResponse,
  ProofStatus,
  AdvertiserInfo,
  AdvertiserDetailResponse,
  ContactInfo,
  ContactEmailInfo,
  ContactPhoneInfo,
  AgingBalance,
  OrderInfo,
  ActivityInfo,
  DashboardActivityInfo,
  DashboardActivitiesResponse,
  InvoiceInfo,
  PaymentInfo,
  SaveContactRequest,
  SaveContactResponse,
  DeleteContactResponse,
  SetContactInactiveRequest,
  SetContactInactiveResponse,
  UpdateContactAssignmentsRequest,
  ActivityLookupItem,
  ActivityLookupsResponse,
  SaveActivityRequest,
  SaveActivityResponse,
  ContactRoleInfo,
  ContactRolesResponse,
  BillableItemInfo,
  InvoiceDetailResponse,
  PaymentAllocationInfo,
  PaymentDetailResponse,
  ActivityDefaultsResponse,
  CreateActivityRequest,
  SendProofToAdvertiserResponse,
  NewAdInfo,
  NewAdDetailResponse,
  SubmitDesignerNotesResponse,
  RetailRunInfo,
  OrderPublicationInfo,
  OrderImageInfo,
  RetailOrderDetailResponse,
  ReasonInfo,
  OrderLookupsResponse,
  OrderActionResponse,
  AdRunSummaryResponse,
  SalesRepListItem,
  SalesGoalStatus,
  SalesGoalRunItem,
  DashboardMetricItem,
  DashboardMetricsResponse,
  DashboardTouchKpisResponse,
  DashboardTouchKpiDetailResponse,
  MetricDetailItem,
  DealPipelineInfo,
  DealStageInfo,
  LookupItem,
  DealCardInfo,
  DealBoardStageColumn,
  DealBoardResponse,
  DealLookupsResponse,
  DealDetailResponse,
  SaveDealRequest,
  SaveDealResponse,
  MoveDealRequest,
  MoveDealResponse,
  DealDocumentInfo,
  DealDocumentsResponse,
  DocumentContentResponse,
  SearchResultItem,
  SearchMatchType,
  PaymentConfig,
  VaultedCardInfo,
  VaultedAddressInfo,
  VaultedCardsResponse,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
  AddVaultPaymentMethodRequest,
  AddVaultPaymentMethodResponse,
  DeleteVaultPaymentMethodRequest,
  DeleteVaultPaymentMethodResponse,
  UpdateVaultExpirationRequest,
  UpdateVaultExpirationResponse,
  AdvertiserEditFormResponse,
  AdvertiserEditPermissions,
  AdvertiserEditData,
  SaveAdvertiserRequest,
  SaveAdvertiserResponse,
  CreateAdvertiserResponse,
  SetDesignReadyResponse,
  CreateOrderLookupsResponse,
  RateCardDetailResponse,
  PublicationCalendarRequest,
  PublicationCalendarResponse,
  SchedulePreviewRequest,
  SchedulePreviewResponse,
  CreateOrderRequest,
  CreateOrderResponse,
  CreateOrderFromRunsRequest,
  PublicationFilteredItemsResponse,
  DocumentCategoryInfo,
  UploadDocumentResponse,
  AdvertiserDocumentInfo,
  BlitzAdvertiserInfo,
  BlitzContactInfo
} from '@/types'

// ============================================================================
// Error type
// ============================================================================

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Creates a client whose requests belong to one visit. Business writes are never replayed. */
function createClient(): AxiosInstance {
  const baseURL = import.meta.env.DEV ? '/sr' : `${getApiBaseUrl()}/sr`
  const client = axios.create({ baseURL, timeout: 30000 })
  const generation = authenticationGeneration()
  client.interceptors.request.use(config => {
    const authenticationCall = config.url?.startsWith('/auth/')
    if (!authenticationCall && protectedActionsPaused.value) {
      throw new ApiError('Verify your email or restore your session before continuing. Your changes have not been resubmitted.', 401)
    }
    config.withCredentials = authenticationCall
    config.headers['X-Api-Key'] = getApiKey()
    config.headers['X-Tenant-Id'] = getTenantId()
    const token = getSessionToken()
    if (token) config.headers['X-Sales-Rep-Token'] = token
    return config
  })
  client.interceptors.response.use(response => {
    if (generation !== authenticationGeneration() && !response.config.url?.startsWith('/auth/')) {
      throw new ApiError('This response belongs to a session that has ended. Check the saved result before submitting again.', 409)
    }
    return response
  }, error => {
    if (error instanceof ApiError) throw error
    const status = error.response?.status ?? 0
    const data = error.response?.data
    const outcome = data?.outcome ?? data?.Outcome
    const message = data?.message ?? data?.Message ?? (status === 0 || status === 503
      ? 'SalesScout could not reach the server. Check your connection and retry. A submitted change may already have completed.'
      : status === 403 ? 'You do not have permission to perform this action.' : 'The request could not be completed. Please try again.')
    const authenticationCall = error.config?.url?.startsWith('/auth/')
    const sessionFailure = status === 401 || (status === 403 && outcome === 'session_released')
    if ((!authenticationCall || error.config?.url === '/auth/keep-alive') && sessionFailure
        && generation === authenticationGeneration() && getSessionToken()) {
      reportAuthenticationFailure({ outcome: outcome ?? 'session_inactive', message })
    }
    throw new ApiError(message, status)
  })
  return client
}

// Lazy client — recreated on each call so it picks up the latest config
// (config loads async before first real call)
export function api(): AxiosInstance {
  return createClient()
}

// ============================================================================
// Auth endpoints
// ============================================================================

/**
 * Request a verification code be sent to the sales rep's email.
 * Backend validates the email exists in the tenant DB and has a valid license.
 */
export async function requestLoginCode(email: string, captchaToken?: string): Promise<LoginResponse> {
  const req: LoginRequest = { email, captchaToken }
  const { data } = await api().post<LoginResponse>('/auth/request-code', req)
  return {
    success: data.success ?? (data as unknown as { Success?: boolean }).Success ?? false,
    message: data.message ?? (data as unknown as { Message?: string }).Message,
    emailSent: data.emailSent ?? (data as unknown as { EmailSent?: boolean }).EmailSent ?? false,
    errorCode: data.errorCode ?? (data as unknown as { ErrorCode?: string }).ErrorCode
  }
}

/**
 * Verify the code entered by the sales rep.
 * On success, returns a session token and the sales rep info.
 */
export async function verifyLoginCode(email: string, code: string, forceLogin?: boolean, rememberDevice = false, replaceSessionId?: number): Promise<VerifyCodeResponse> {
  const req: VerifyCodeRequest = { email, code, rememberDevice, replaceSessionId }
  if (forceLogin) req.forceLogin = true
  const { data, status } = await api().post<VerifyCodeResponse>('/auth/verify-code', req, { validateStatus: status => status < 600 })
  return authenticationResponse(data, status)
}

/** Cookie authentication responses retain typed failures, including retryable outages. */
export async function rememberedRequest(action: 'resume' | 'activity', replaceSessionId?: number): Promise<VerifyCodeResponse> {
  const { data, status } = await api().post('/auth/' + action, { replaceSessionId }, { validateStatus: status => status < 600 })
  return authenticationResponse(data, status)
}

/** An infrastructure error page has no lifecycle payload, but still must preserve remembered access. */
function authenticationResponse(data: unknown, status: number): VerifyCodeResponse {
  const result = convertVerifyResponse(data || {})
  if (status >= 500) {
    result.success = false
    result.outcome ||= 'temporarily_unavailable'
    result.message ||= 'SalesScout is temporarily unavailable. Your remembered sign-in is retained. Please retry.'
  }
  return result
}

/** Reads the deployment capability; older APIs leave the option unavailable. */
export async function rememberedSignInAvailable(): Promise<boolean> {
  try {
    const { data } = await api().get('/auth/options')
    return (data.rememberedSignIn ?? data.RememberedSignIn) === true
  } catch { return false }
}

/** Acknowledges a durable server operation. Network failures must remain queued locally. */
export async function endDeviceSession(action: 'logout' | 'forget-device' | 'forget-other-devices', deviceId?: string): Promise<boolean> {
  const { data } = await api().post('/auth/' + action, { deviceId })
  return (data?.releasePending ?? data?.ReleasePending) === true
}

function convertVerifyResponse(data: unknown): VerifyCodeResponse {
  const d = data as Record<string, unknown>
  const rep = (d.salesRep ?? d.SalesRep) as Record<string, unknown> | undefined
  return {
    success: (d.success ?? d.Success ?? false) as boolean,
    outcome: (d.outcome ?? d.Outcome) as string | undefined,
    remembered: (d.remembered ?? d.Remembered) as boolean | undefined,
    deviceId: (d.deviceId ?? d.DeviceId) as string | undefined,
    activeUntilUtc: (d.activeUntilUtc ?? d.ActiveUntilUtc) as string | undefined,
    rememberedUntilUtc: (d.rememberedUntilUtc ?? d.RememberedUntilUtc) as string | undefined,
    reverifyAtUtc: (d.reverifyAtUtc ?? d.ReverifyAtUtc) as string | undefined,
    replaceSessionId: (d.replaceSessionId ?? d.ReplaceSessionId) as number | undefined,
    sessionToken: (d.sessionToken ?? d.SessionToken) as string | undefined,
    message: (d.message ?? d.Message) as string | undefined,
    salesRep: rep ? convertSalesRepInfo(rep) : undefined,
    licenseLimitReached: (d.licenseLimitReached ?? d.LicenseLimitReached ?? false) as boolean,
    activeUsers: (d.activeUsers ?? d.ActiveUsers) as number | undefined,
    maxUsers: (d.maxUsers ?? d.MaxUsers) as number | undefined,
    userHasExistingSession: (d.userHasExistingSession ?? d.UserHasExistingSession ?? false) as boolean,
    userExistingSessionCount: (d.userExistingSessionCount ?? d.UserExistingSessionCount) as number | undefined
  }
}

function convertSalesRepInfo(d: Record<string, unknown>): SalesRepInfo {
  return {
    salesRepKey: (d.salesRepKey ?? d.SalesRepKey ?? 0) as number,
    userKey: (d.userKey ?? d.UserKey ?? null) as number | null,
    name: (d.name ?? d.Name ?? '') as string,
    email: (d.email ?? d.Email ?? '') as string,
    isAdmin: (d.isAdmin ?? d.IsAdmin ?? false) as boolean,
    publicationKeys: ((d.publicationKeys ?? d.PublicationKeys ?? []) as number[]),
    canViewOtherAdvertisers: (d.canViewOtherAdvertisers ?? d.CanViewOtherAdvertisers ?? false) as boolean,
    canCreatePayment: (d.canCreatePayment ?? d.CanCreatePayment ?? false) as boolean,
    canEditAccount: (d.canEditAccount ?? d.CanEditAccount ?? false) as boolean,
    canCreateAccount: (d.canCreateAccount ?? d.CanCreateAccount ?? false) as boolean
  }
}

/**
 * Notify the server that the sales rep is logging out.
 * Releases the floating license slot. Best-effort — errors are silently ignored
 * so that the local session is always cleared regardless of server reachability.
 */
/** Renews the server lease; failures use the standard expired-session handling. */
export async function keepSessionAlive(): Promise<void> {
  try { await api().post('/auth/keep-alive') } catch { /* The interceptor reports expired sessions. */ }
}

export async function logoutFromServer(): Promise<void> {
  try {
    await api().post('/auth/logout')
  } catch {
    // Intentional: don't block local logout if the server call fails
  }
}

/**
 * Sends a fire-and-forget logout beacon when the browser tab or window is closing.
 * Uses navigator.sendBeacon with a Blob so we can set custom headers via query params
 * (sendBeacon doesn't support custom headers). The server accepts the token as a query
 * param specifically for this use case.
 *
 * Returns true if the beacon was queued, false otherwise.
 */
export function beaconLogout(sessionToken?: string): boolean {
  const token = sessionToken ?? getSessionToken()
  if (!token) return false

  const baseURL = import.meta.env.DEV ? '/sr' : `${getApiBaseUrl()}/sr`
  const params = new URLSearchParams({
    apiKey: getApiKey(),
    tenantId: getTenantId(),
    salesRepToken: token
  })

  try {
    // Pass an empty Blob (not just the URL) so sendBeacon sets a Content-Length
    // header. IIS rejects POST requests without Content-Length with HTTP 411.
    const blob = new Blob([''], { type: 'text/plain' })
    return navigator.sendBeacon(`${baseURL}/auth/logout-beacon?${params.toString()}`, blob)
  } catch {
    return false
  }
}

/**
 * Demo login — skips OTP email verification.
 * Used by internal team for demonstrations against customer databases.
 * Requires the demo secret (access key) to be provided.
 */
export async function demoLogin(email: string, demoSecret: string): Promise<VerifyCodeResponse> {
  const { data } = await api().post<Record<string, unknown>>('/auth/demo-login',
    { Email: email },
    { headers: { 'X-Demo-Secret': demoSecret } }
  )
  return convertVerifyResponse(data)
}

/**
 * Dev-only auto-login — skips OTP entirely, only works on localhost.
 */
export async function devAutoLogin(email: string): Promise<VerifyCodeResponse> {
  const { data } = await api().post<Record<string, unknown>>('/auth/dev-auto-login', { Email: email })
  return convertVerifyResponse(data)
}

// ============================================================================
// Dashboard endpoints
// ============================================================================


export async function getSalesGoalStatus(): Promise<SalesGoalStatus[]> {
  const { data } = await api().get<Record<string, unknown>>('/dashboard/sales-goals')
  const raw = (data.goals ?? data.Goals ?? []) as Record<string, unknown>[]
  return raw.map(d => ({
    salesGoalKey: (d.salesGoalKey ?? d.SalesGoalKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    goalProfileName: (d.goalProfileName ?? d.GoalProfileName ?? '') as string,
    periodDisplay: (d.periodDisplay ?? d.PeriodDisplay ?? '') as string,
    periodDateRange: (d.periodDateRange ?? d.PeriodDateRange ?? '') as string,
    periodType: (d.periodType ?? d.PeriodType ?? '') as string,
    goalAmount: (d.goalAmount ?? d.GoalAmount ?? 0) as number,
    achievedToDate: (d.achievedToDate ?? d.AchievedToDate ?? 0) as number,
    achievedFuture: (d.achievedFuture ?? d.AchievedFuture ?? 0) as number,
    upcomingQuotes: (d.upcomingQuotes ?? d.UpcomingQuotes ?? 0) as number,
    pastQuotes: (d.pastQuotes ?? d.PastQuotes ?? 0) as number,
    targetToDate: (d.targetToDate ?? d.TargetToDate ?? 0) as number,
    percentageOfPeriodElapsed: (d.percentageOfPeriodElapsed ?? d.PercentageOfPeriodElapsed ?? 0) as number,
    percentageAchieved: (d.percentageAchieved ?? d.PercentageAchieved ?? 0) as number,
    onTrack: (d.onTrack ?? d.OnTrack ?? false) as boolean,
    amountBehind: (d.amountBehind ?? d.AmountBehind ?? 0) as number,
    amountAhead: (d.amountAhead ?? d.AmountAhead ?? 0) as number,
    remainingAmount: (d.remainingAmount ?? d.RemainingAmount ?? 0) as number
  }))
}

export async function getSalesGoalRunDetails(salesGoalKey: number, category: string): Promise<SalesGoalRunItem[]> {
  const { data } = await api().get<Record<string, unknown>>(`/dashboard/sales-goals/${salesGoalKey}/runs`, {
    params: { category }
  })
  const raw = (data.runs ?? data.Runs ?? []) as Record<string, unknown>[]
  return raw.map(d => ({
    productType: (d.productType ?? d.ProductType ?? '') as string,
    advertiserKey: (d.advertiserKey ?? d.AdvertiserKey ?? 0) as number,
    advertiserName: (d.advertiserName ?? d.AdvertiserName ?? '') as string,
    orderNumber: (d.orderNumber ?? d.OrderNumber ?? '') as string,
    runDate: (d.runDate ?? d.RunDate ?? '') as string,
    amount: (d.amount ?? d.Amount ?? 0) as number,
    description: (d.description ?? d.Description ?? '') as string,
    publicationName: (d.publicationName ?? d.PublicationName ?? '') as string
  }))
}


/**
 * Checks a resolved dashboard date without accepting normalized invalid calendar days.
 * @param value The date received from the metrics endpoint.
 * @returns Whether the value is a valid calendar date in yyyy-MM-dd format.
 */
function isDashboardCalendarDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^(?!0000)\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
}

export async function getDashboardMetrics(period: string = 'ThisWeek', dormantPeriod: string = '1-30'): Promise<DashboardMetricsResponse> {
  const { data } = await api().get<Record<string, unknown>>('/dashboard/metrics', {
    params: { period, dormantPeriod }
  })

  const periodStart = data.periodStart ?? data.PeriodStart
  const periodEnd = data.periodEnd ?? data.PeriodEnd
  const dormantPeriodStart = data.dormantPeriodStart ?? data.DormantPeriodStart
  const dormantPeriodEnd = data.dormantPeriodEnd ?? data.DormantPeriodEnd
  if (!isDashboardCalendarDate(periodStart) || !isDashboardCalendarDate(periodEnd)
    || !isDashboardCalendarDate(dormantPeriodStart) || !isDashboardCalendarDate(dormantPeriodEnd)
    || periodStart > periodEnd || dormantPeriodStart > dormantPeriodEnd) {
    throw new Error('Dashboard dates are unavailable or invalid. Please refresh.')
  }

  function mapMetricItem(raw: Record<string, unknown> | null | undefined): DashboardMetricItem {
    if (!raw) return { count: 0, amount: 0 }
    return {
      count: (raw.count ?? raw.Count ?? 0) as number,
      amount: (raw.amount ?? raw.Amount ?? 0) as number
    }
  }

  return {
    period: (data.period ?? data.Period ?? 'ThisWeek') as string,
    periodLabel: (data.periodLabel ?? data.PeriodLabel ?? '') as string,
    periodStart,
    periodEnd,
    dormantPeriodStart,
    dormantPeriodEnd,
    priceQuotes: mapMetricItem((data.priceQuotes ?? data.PriceQuotes) as Record<string, unknown>),
    deals: mapMetricItem((data.deals ?? data.Deals) as Record<string, unknown>),
    dealsWon: mapMetricItem((data.dealsWon ?? data.DealsWon) as Record<string, unknown>),
    dealsLost: mapMetricItem((data.dealsLost ?? data.DealsLost) as Record<string, unknown>),
    expiringOrders: mapMetricItem((data.expiringOrders ?? data.ExpiringOrders) as Record<string, unknown>),
    dormantAdvertisers: mapMetricItem((data.dormantAdvertisers ?? data.DormantAdvertisers) as Record<string, unknown>),
    holds: mapMetricItem((data.holds ?? data.Holds) as Record<string, unknown>),
    cancelledRuns: mapMetricItem((data.cancelledRuns ?? data.CancelledRuns) as Record<string, unknown>),
    revenue: mapMetricItem((data.revenue ?? data.Revenue) as Record<string, unknown>)
  }
}

export async function getDashboardTouchKpis(): Promise<DashboardTouchKpisResponse> {
  const { data } = await api().get<Record<string, unknown>>('/dashboard/touch-kpis')
  return {
    asOfDate: (data.asOfDate ?? data.AsOfDate ?? '') as string,
    newProspectContacts: (data.newProspectContacts ?? data.NewProspectContacts ?? 0) as number,
    followUps: (data.followUps ?? data.FollowUps ?? 0) as number,
    relationshipTouchpoints: (data.relationshipTouchpoints ?? data.RelationshipTouchpoints ?? 0) as number
  }
}

export async function getDashboardTouchKpiDetails(kpiType: string): Promise<DashboardTouchKpiDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/dashboard/touch-kpis/${kpiType}/details`)
  const rawItems = (data.items ?? data.Items ?? []) as Record<string, unknown>[]
  return {
    kpiType: (data.kpiType ?? data.KpiType ?? '') as string,
    label: (data.label ?? data.Label ?? '') as string,
    asOfDate: (data.asOfDate ?? data.AsOfDate ?? '') as string,
    items: rawItems.map(convertDashboardActivityInfo)
  }
}


export async function getDashboardMetricDetails(metricType: string, period: string = 'ThisWeek', dormantPeriod: string = '1-30'): Promise<MetricDetailItem[]> {
  const { data } = await api().get<Record<string, unknown>>(`/dashboard/metrics/${metricType}/details`, {
    params: { period, dormantPeriod }
  })
  const raw = (data.items ?? data.Items ?? []) as Record<string, unknown>[]
  return raw.map(d => ({
    itemType: (d.itemType ?? d.ItemType ?? '') as string,
    itemKey: (d.itemKey ?? d.ItemKey ?? 0) as number,
    salesRepName: (d.salesRepName ?? d.SalesRepName ?? '') as string,
    canOpenAdvertiser: (d.canOpenAdvertiser ?? d.CanOpenAdvertiser ?? false) as boolean,
    canOpenOrder: (d.canOpenOrder ?? d.CanOpenOrder ?? false) as boolean,
    advertiserKey: (d.advertiserKey ?? d.AdvertiserKey ?? 0) as number,
    advertiserName: (d.advertiserName ?? d.AdvertiserName ?? '') as string,
    orderNumber: (d.orderNumber ?? d.OrderNumber ?? '') as string,
    date: (d.date ?? d.Date ?? '') as string,
    amount: (d.amount ?? d.Amount ?? 0) as number,
    description: (d.description ?? d.Description ?? '') as string,
    publicationName: (d.publicationName ?? d.PublicationName ?? '') as string,
    adSize: (d.adSize ?? d.AdSize ?? '') as string,
    dealName: (d.dealName ?? d.DealName ?? '') as string,
    dealStageName: (d.dealStageName ?? d.DealStageName ?? '') as string,
    dealKey: (d.dealKey ?? d.DealKey ?? null) as number | null,
    orderKey: (d.orderKey ?? d.OrderKey ?? null) as number | null,
    holdReasonName: (d.holdReasonName ?? d.HoldReasonName ?? '') as string
  }))
}

function convertDashboardActivityInfo(d: Record<string, unknown>): DashboardActivityInfo {
  const rawPhones = ((d.contactPhoneNumbers ?? d.ContactPhoneNumbers ?? []) as Record<string, unknown>[])
  return {
    activityKey: (d.activityKey ?? d.ActivityKey ?? 0) as number,
    startTime: (d.startTime ?? d.StartTime ?? null) as string | null,
    endTime: (d.endTime ?? d.EndTime ?? null) as string | null,
    startTimeHasTimeOfDay: (d.startTimeHasTimeOfDay ?? d.StartTimeHasTimeOfDay ?? false) as boolean,
    endTimeHasTimeOfDay: (d.endTimeHasTimeOfDay ?? d.EndTimeHasTimeOfDay ?? false) as boolean,
    title: (d.title ?? d.Title ?? '') as string,
    contactName: (d.contactName ?? d.ContactName ?? '') as string,
    contactFKey: (d.contactFKey ?? d.ContactFKey ?? null) as number | null,
    activityTypeName: (d.activityTypeName ?? d.ActivityTypeName ?? '') as string,
    activityTypeFKey: (d.activityTypeFKey ?? d.ActivityTypeFKey ?? null) as number | null,
    activityStatusName: (d.activityStatusName ?? d.ActivityStatusName ?? '') as string,
    activityStatusFKey: (d.activityStatusFKey ?? d.ActivityStatusFKey ?? null) as number | null,
    activityPriorityFKey: (d.activityPriorityFKey ?? d.ActivityPriorityFKey ?? null) as number | null,
    activityPriorityName: (d.activityPriorityName ?? d.ActivityPriorityName ?? '') as string,
    notes: (d.notes ?? d.Notes ?? '') as string,
    isClosed: (d.isClosed ?? d.IsClosed ?? false) as boolean,
    assignedToSalesRepKey: (d.assignedToSalesRepKey ?? d.AssignedToSalesRepKey ?? null) as number | null,
    assignedToName: (d.assignedToName ?? d.AssignedToName ?? '') as string,
    dealFKey: (d.dealFKey ?? d.DealFKey ?? null) as number | null,
    dealName: (d.dealName ?? d.DealName ?? '') as string,
    advertiserKey: (d.advertiserKey ?? d.AdvertiserKey ?? null) as number | null,
    advertiserName: (d.advertiserName ?? d.AdvertiserName ?? '') as string,
    contactPhoneNumbers: rawPhones.map(p => ({
      phoneNumber: (p.phoneNumber ?? p.PhoneNumber ?? '') as string,
      description: (p.description ?? p.Description ?? '') as string,
      isPrimary: (p.isPrimary ?? p.IsPrimary ?? false) as boolean
    })),
    lastTouchDate: (d.lastTouchDate ?? d.LastTouchDate ?? null) as string | null,
    overdueTouchCount: (d.overdueTouchCount ?? d.OverdueTouchCount ?? 0) as number,
    nextTouchDate: (d.nextTouchDate ?? d.NextTouchDate ?? null) as string | null,
    nextDealCloseDate: (d.nextDealCloseDate ?? d.NextDealCloseDate ?? null) as string | null
  }
}

export async function getDashboardActivities(upcomingDays = 7): Promise<DashboardActivitiesResponse> {
  const { data } = await api().get<Record<string, unknown>>('/dashboard/activities', { params: { upcomingDays } })
  const mapList = (key: string): DashboardActivityInfo[] => {
    const lc = key.charAt(0).toLowerCase() + key.slice(1)
    const uc = key.charAt(0).toUpperCase() + key.slice(1)
    return ((data[lc] ?? data[uc] ?? []) as Record<string, unknown>[]).map(convertDashboardActivityInfo)
  }
  return {
    todayActivities: mapList('todayActivities'),
    tomorrowActivities: mapList('tomorrowActivities'),
    upcomingActivities: mapList('upcomingActivities'),
    overdueActivities: mapList('overdueActivities'),
    completedTodayActivities: mapList('completedTodayActivities')
  }
}

export async function markActivityDone(activityKey: number): Promise<{ success: boolean; message: string }> {
  const { data } = await api().post<Record<string, unknown>>(`/activities/${activityKey}/mark-done`)
  return {
    success: (data.Success ?? data.success) as boolean,
    message: (data.Message ?? data.message ?? '') as string
  }
}

export async function getActivityNotesHtml(activityKey: number): Promise<string> {
  const { data } = await api().get<Record<string, unknown>>(`/activities/${activityKey}/notes-html`)
  return (data.NotesHtml ?? data.notesHtml ?? '') as string
}

export async function saveActivityNotesFormatted(activityKey: number, notesHtml: string): Promise<SaveActivityResponse> {
  const { data } = await api().put<Record<string, unknown>>(`/activities/${activityKey}/notes-formatted`, {
    NotesHtml: notesHtml
  })
  if (!(data.Success ?? data.success)) throw new ApiError(String(data.Message ?? data.message ?? 'Formatted notes could not be saved. Please retry.'), 422)
  return {
    success: (data.Success ?? data.success ?? false) as boolean,
    message: (data.Message ?? data.message ?? '') as string
  }
}

// ============================================================================
// Proof endpoints
// ============================================================================

function convertProofInfo(d: Record<string, unknown>): ProofInfo {
  return {
    proofKey: (d.proofKey ?? d.ProofKey ?? 0) as number,
    retailOrderFKey: (d.retailOrderFKey ?? d.RetailOrderFKey ?? 0) as number,
    imageNumber: (d.imageNumber ?? d.ImageNumber ?? null) as number | null,
    orderNumber: (d.orderNumber ?? d.OrderNumber ?? '') as string,
    advertiserName: (d.advertiserName ?? d.AdvertiserName ?? '') as string,
    salesRepName: (d.salesRepName ?? d.SalesRepName ?? '') as string,
    description: (d.description ?? d.Description ?? '') as string,
    firstRunDate: (d.firstRunDate ?? d.FirstRunDate ?? null) as string | null,
    lastRunDate: (d.lastRunDate ?? d.LastRunDate ?? null) as string | null,
    publications: (d.publications ?? d.Publications ?? '') as string,
    adSize: (d.adSize ?? d.AdSize ?? '') as string,
    status: (d.status ?? d.Status ?? 'AwaitingSalesRepReview') as ProofStatus,
    statusDisplay: (d.statusDisplay ?? d.StatusDisplay ?? '') as string,
    proofImageUrl: (d.proofImageUrl ?? d.ProofImageUrl ?? '') as string,
    numberOfProofsSent: (d.numberOfProofsSent ?? d.NumberOfProofsSent ?? 0) as number
  }
}

export async function getProofSummary(range: ProofDateRange = {}): Promise<ProofSummaryResponse> {
  const { data } = await api().get<Record<string, unknown>>('/proofs/summary', { params: range })
  return {
    awaitingSalesRepCount: (data.awaitingSalesRepCount ?? data.AwaitingSalesRepCount ?? 0) as number,
    awaitingAdvertiserCount: (data.awaitingAdvertiserCount ?? data.AwaitingAdvertiserCount ?? 0) as number,
    needsChangesCount: (data.needsChangesCount ?? data.NeedsChangesCount ?? 0) as number,
    totalActiveCount: (data.totalActiveCount ?? data.TotalActiveCount ?? 0) as number
  }
}

export async function getProofs(range: ProofDateRange = {}): Promise<ProofListResponse> {
  const { data } = await api().get<Record<string, unknown>>('/proofs', { params: range })
  const activeRaw = (data.activeProofs ?? data.ActiveProofs ?? []) as Record<string, unknown>[]
  const completedRaw = (data.recentlyCompletedProofs ?? data.RecentlyCompletedProofs ?? []) as Record<string, unknown>[]
  return {
    activeProofs: activeRaw.map(convertProofInfo),
    recentlyCompletedProofs: completedRaw.map(convertProofInfo),
    awaitingSalesRepCount: (data.awaitingSalesRepCount ?? data.AwaitingSalesRepCount ?? 0) as number,
    awaitingAdvertiserCount: (data.awaitingAdvertiserCount ?? data.AwaitingAdvertiserCount ?? 0) as number
  }
}

export async function getProofDetail(proofKey: number): Promise<ProofDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/proofs/${proofKey}`)
  return {
    proofKey: (data.proofKey ?? data.ProofKey ?? 0) as number,
    retailOrderFKey: (data.retailOrderFKey ?? data.RetailOrderFKey ?? 0) as number,
    imageNumber: (data.imageNumber ?? data.ImageNumber ?? null) as number | null,
    orderNumber: (data.orderNumber ?? data.OrderNumber ?? '') as string,
    advertiserName: (data.advertiserName ?? data.AdvertiserName ?? '') as string,
    salesRepName: (data.salesRepName ?? data.SalesRepName ?? '') as string,
    salesRepNames: (data.salesRepNames ?? data.SalesRepNames ?? '') as string,
    description: (data.description ?? data.Description ?? '') as string,
    firstRunDate: (data.firstRunDate ?? data.FirstRunDate ?? null) as string | null,
    lastRunDate: (data.lastRunDate ?? data.LastRunDate ?? null) as string | null,
    publications: (data.publications ?? data.Publications ?? '') as string,
    publicationAbbreviations: (data.publicationAbbreviations ?? data.PublicationAbbreviations ?? '') as string,
    adSize: (data.adSize ?? data.AdSize ?? '') as string,
    status: (data.status ?? data.Status ?? 'AwaitingSalesRepReview') as ProofStatus,
    statusDisplay: (data.statusDisplay ?? data.StatusDisplay ?? '') as string,
    proofImageUrl: (data.proofImageUrl ?? data.ProofImageUrl ?? '') as string,
    numberOfProofsSent: (data.numberOfProofsSent ?? data.NumberOfProofsSent ?? 0) as number,
    canApprove: (data.canApprove ?? data.CanApprove ?? false) as boolean,
    canRequestChanges: (data.canRequestChanges ?? data.CanRequestChanges ?? false) as boolean,
    canSendToAdvertiser: (data.canSendToAdvertiser ?? data.CanSendToAdvertiser ?? false) as boolean,
    canEmailAd: (data.canEmailAd ?? data.CanEmailAd ?? false) as boolean,
    advertiserKey: (data.advertiserKey ?? data.AdvertiserKey ?? 0) as number,
    advertiserEmail: (data.advertiserEmail ?? data.AdvertiserEmail ?? '') as string,
    advertiserContactName: (data.advertiserContactName ?? data.AdvertiserContactName ?? '') as string
  }
}

export async function approveProof(proofKey: number): Promise<ApproveProofResponse> {
  const { data } = await api().post<Record<string, unknown>>('/proofs/approve', { ProofKey: proofKey })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    newStatus: (data.newStatus ?? data.NewStatus) as string | undefined,
    newStatusDisplay: (data.newStatusDisplay ?? data.NewStatusDisplay) as string | undefined
  }
}

export async function requestProofChanges(
  proofKey: number,
  requestedChanges: string,
  files?: File[]
): Promise<RequestChangesResponse> {
  const formData = new FormData()
  formData.append('proofKey', proofKey.toString())
  formData.append('requestedChanges', requestedChanges)
  if (files) {
    for (const file of files) {
      formData.append('files', file, file.name)
    }
  }
  const { data } = await api().post<Record<string, unknown>>('/proofs/request-changes', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    newStatus: (data.newStatus ?? data.NewStatus) as string | undefined,
    newStatusDisplay: (data.newStatusDisplay ?? data.NewStatusDisplay) as string | undefined
  }
}

export async function sendProofToAdvertiser(proofKey: number, emailOnly?: boolean): Promise<SendProofToAdvertiserResponse> {
  const { data } = await api().post<Record<string, unknown>>('/proofs/send-to-advertiser', { ProofKey: proofKey, EmailOnly: emailOnly ?? false })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    newStatus: (data.newStatus ?? data.NewStatus) as string | undefined,
    newStatusDisplay: (data.newStatusDisplay ?? data.NewStatusDisplay) as string | undefined,
    numberOfProofsSent: (data.numberOfProofsSent ?? data.NumberOfProofsSent) as number | undefined
  }
}

// ============================================================================
// New Ads endpoints
// ============================================================================

function convertNewAdInfo(d: Record<string, unknown>): NewAdInfo {
  return {
    retailOrderImageKey: (d.retailOrderImageKey ?? d.RetailOrderImageKey ?? 0) as number,
    retailOrderFKey: (d.retailOrderFKey ?? d.RetailOrderFKey ?? 0) as number,
    imageNumber: (d.imageNumber ?? d.ImageNumber ?? null) as number | null,
    orderNumber: (d.orderNumber ?? d.OrderNumber ?? '') as string,
    advertiserName: (d.advertiserName ?? d.AdvertiserName ?? '') as string,
    salesRepName: (d.salesRepName ?? d.SalesRepName ?? '') as string,
    description: (d.description ?? d.Description ?? '') as string,
    firstRunDate: (d.firstRunDate ?? d.FirstRunDate ?? null) as string | null,
    lastRunDate: (d.lastRunDate ?? d.LastRunDate ?? null) as string | null,
    publications: (d.publications ?? d.Publications ?? '') as string,
    adSize: (d.adSize ?? d.AdSize ?? '') as string,
    status: (d.status ?? d.Status ?? '') as string,
    statusDisplay: (d.statusDisplay ?? d.StatusDisplay ?? '') as string,
    canAddNotes: (d.canAddNotes ?? d.CanAddNotes ?? false) as boolean
  }
}

export async function getNewAds(range: ProofDateRange = {}): Promise<{
  newAds: NewAdInfo[]; newAdCount: number;
  designReadyAds: NewAdInfo[]; designReadyAdCount: number;
  inComposingAds: NewAdInfo[]; inComposingAdCount: number;
}> {
  const { data } = await api().get<Record<string, unknown>>('/new-ads', { params: range })
  const rawAds = (data.newAds ?? data.NewAds ?? []) as Record<string, unknown>[]
  const rawDesignReady = (data.designReadyAds ?? data.DesignReadyAds ?? []) as Record<string, unknown>[]
  const rawInComposing = (data.inComposingAds ?? data.InComposingAds ?? []) as Record<string, unknown>[]
  return {
    newAds: rawAds.map(convertNewAdInfo),
    newAdCount: (data.newAdCount ?? data.NewAdCount ?? 0) as number,
    designReadyAds: rawDesignReady.map(convertNewAdInfo),
    designReadyAdCount: (data.designReadyAdCount ?? data.DesignReadyAdCount ?? 0) as number,
    inComposingAds: rawInComposing.map(convertNewAdInfo),
    inComposingAdCount: (data.inComposingAdCount ?? data.InComposingAdCount ?? 0) as number
  }
}

export async function getNewAdDetail(orderImageKey: number): Promise<NewAdDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/new-ads/${orderImageKey}`)
  return convertNewAdInfo(data) as NewAdDetailResponse
}

export async function submitDesignerNotes(
  orderImageKey: number,
  notes: string,
  files?: File[]
): Promise<SubmitDesignerNotesResponse> {
  const formData = new FormData()
  formData.append('orderImageKey', orderImageKey.toString())
  formData.append('notes', notes)
  if (files) {
    for (const file of files) {
      formData.append('files', file, file.name)
    }
  }
  const { data } = await api().post<Record<string, unknown>>('/new-ads/notes', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

export async function setAdDesignReady(orderImageKey: number): Promise<SetDesignReadyResponse> {
  const { data } = await api().post<Record<string, unknown>>(`/new-ads/${orderImageKey}/set-design-ready`)
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

/**
 * Fetch the proof PDF preview as a blob and return an object URL for img src.
 * Returns null if the preview cannot be loaded.
 */
export async function getProofPreviewUrl(proofKey: number): Promise<string | null> {
  try {
    const response = await api().get(`/proofs/${proofKey}/preview`, {
      responseType: 'blob'
    })
    const blob = new Blob([response.data], { type: 'image/jpeg' })
    return URL.createObjectURL(blob)
  } catch (err) {
    console.error(`[api] Failed to load proof preview for ${proofKey}:`, err)
    return null
  }
}

/**
 * Fetch the repository image for a completed proof (Approved/ReadyToPublish).
 * These proofs have their image in the Azure File Share repository, not blob storage.
 * Returns an object URL for img src, or null if not available.
 */
export async function getRepositoryImageUrl(proofKey: number): Promise<string | null> {
  try {
    const response = await api().get(`/proofs/${proofKey}/repository-image`, {
      responseType: 'blob'
    })
    const contentType = response.headers['content-type'] || 'image/jpeg'
    const blob = new Blob([response.data], { type: contentType })
    return URL.createObjectURL(blob)
  } catch {
    return null
  }
}

// ============================================================================
// Accounts endpoints
// ============================================================================

function convertAdvertiserInfo(d: Record<string, unknown>): AdvertiserInfo {
  return {
    advertiserKey: (d.advertiserKey ?? d.AdvertiserKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    addressee: (d.addressee ?? d.Addressee ?? '') as string,
    email: (d.email ?? d.Email ?? '') as string,
    phone: (d.phone ?? d.Phone ?? '') as string,
    city: (d.city ?? d.City ?? '') as string,
    state: (d.state ?? d.State ?? '') as string,
    accountBalance: (d.accountBalance ?? d.AccountBalance ?? 0) as number,
    nextRunDate: (d.nextRunDate ?? d.NextRunDate ?? null) as string | null,
    nextRunIsOnHold: (d.nextRunIsOnHold ?? d.NextRunIsOnHold ?? false) as boolean,
    lastRunDate: (d.lastRunDate ?? d.LastRunDate ?? null) as string | null,
    lastTouchDate: (d.lastTouchDate ?? d.LastTouchDate ?? null) as string | null,
    overdueTouchCount: (d.overdueTouchCount ?? d.OverdueTouchCount ?? 0) as number,
    nextTouchDate: (d.nextTouchDate ?? d.NextTouchDate ?? null) as string | null,
    nextDealCloseDate: (d.nextDealCloseDate ?? d.NextDealCloseDate ?? null) as string | null,
    salesRepName: (d.salesRepName ?? d.SalesRepName ?? null) as string | null
  }
}

function convertContactEmailInfo(d: Record<string, unknown>): ContactEmailInfo {
  return {
    emailAddress: (d.emailAddress ?? d.EmailAddress ?? '') as string,
    description: (d.description ?? d.Description ?? '') as string,
    isPrimary: (d.isPrimary ?? d.IsPrimary ?? false) as boolean
  }
}

function convertContactPhoneInfo(d: Record<string, unknown>): ContactPhoneInfo {
  return {
    phoneNumber: (d.phoneNumber ?? d.PhoneNumber ?? '') as string,
    description: (d.description ?? d.Description ?? '') as string,
    isPrimary: (d.isPrimary ?? d.IsPrimary ?? false) as boolean
  }
}

function convertContactInfo(d: Record<string, unknown>): ContactInfo {
  const emailsRaw = (d.emails ?? d.Emails ?? []) as Record<string, unknown>[]
  const phonesRaw = (d.phoneNumbers ?? d.PhoneNumbers ?? []) as Record<string, unknown>[]
  return {
    contactKey: (d.contactKey ?? d.ContactKey ?? 0) as number,
    firstName: (d.firstName ?? d.FirstName ?? '') as string,
    lastName: (d.lastName ?? d.LastName ?? '') as string,
    displayName: (d.displayName ?? d.DisplayName ?? '') as string,
    nickname: (d.nickname ?? d.Nickname ?? '') as string,
    notes: (d.notes ?? d.Notes ?? '') as string,
    inactive: (d.inactive ?? d.Inactive ?? false) as boolean,
    contactRoleNames: (d.contactRoleNames ?? d.ContactRoleNames ?? '') as string,
    assignedRoleKeys: ((d.assignedRoleKeys ?? d.AssignedRoleKeys ?? []) as number[]),
    emails: emailsRaw.map(convertContactEmailInfo),
    phoneNumbers: phonesRaw.map(convertContactPhoneInfo)
  }
}

function convertAgingBalance(d: Record<string, unknown> | null | undefined): AgingBalance {
  if (!d) return { current: null, over1_30: null, over31_60: null, over61_90: null, over91_120: null, over121Plus: null }
  return {
    current: (d.current ?? d.Current ?? null) as number | null,
    over1_30: (d.over1_30 ?? d.Over1_30 ?? null) as number | null,
    over31_60: (d.over31_60 ?? d.Over31_60 ?? null) as number | null,
    over61_90: (d.over61_90 ?? d.Over61_90 ?? null) as number | null,
    over91_120: (d.over91_120 ?? d.Over91_120 ?? null) as number | null,
    over121Plus: (d.over121Plus ?? d.Over121Plus ?? null) as number | null
  }
}

export async function getAdvertisers(search?: string, showAll?: boolean): Promise<AdvertiserInfo[]> {
  const params: Record<string, string> = {}
  if (search) params.search = search
  if (showAll) params.showAll = 'true'
  const { data } = await api().get<Record<string, unknown>>('/accounts', { params })
  const raw = (data.advertisers ?? data.Advertisers ?? []) as Record<string, unknown>[]
  return raw.map(convertAdvertiserInfo)
}

export async function getOtherAdvertisers(search: string, salesRepKey?: number, showAll?: boolean): Promise<AdvertiserInfo[]> {
  const params: Record<string, string> = {}
  if (search) params.search = search
  if (salesRepKey) params.salesRepKey = String(salesRepKey)
  if (showAll) params.showAll = 'true'
  const { data } = await api().get<Record<string, unknown>>('/accounts/other', { params })
  const raw = (data.advertisers ?? data.Advertisers ?? []) as Record<string, unknown>[]
  return raw.map(convertAdvertiserInfo)
}

function convertBlitzContactInfo(d: Record<string, unknown>): BlitzContactInfo {
  const phonesRaw = (d.phoneNumbers ?? d.PhoneNumbers ?? []) as Record<string, unknown>[]
  return {
    contactKey: (d.contactKey ?? d.ContactKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    phoneNumbers: phonesRaw.map(convertContactPhoneInfo)
  }
}

function convertBlitzAdvertiserInfo(d: Record<string, unknown>): BlitzAdvertiserInfo {
  const contactsRaw = (d.contacts ?? d.Contacts ?? []) as Record<string, unknown>[]
  return {
    advertiserKey: (d.advertiserKey ?? d.AdvertiserKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    addressee: (d.addressee ?? d.Addressee ?? '') as string,
    phone: (d.phone ?? d.Phone ?? '') as string,
    city: (d.city ?? d.City ?? '') as string,
    state: (d.state ?? d.State ?? '') as string,
    accountBalance: (d.accountBalance ?? d.AccountBalance ?? 0) as number,
    lastTouchDate: (d.lastTouchDate ?? d.LastTouchDate ?? null) as string | null,
    overdueTouchCount: (d.overdueTouchCount ?? d.OverdueTouchCount ?? 0) as number,
    nextTouchDate: (d.nextTouchDate ?? d.NextTouchDate ?? null) as string | null,
    nextDealCloseDate: (d.nextDealCloseDate ?? d.NextDealCloseDate ?? null) as string | null,
    nextRunDate: (d.nextRunDate ?? d.NextRunDate ?? null) as string | null,
    nextRunIsOnHold: (d.nextRunIsOnHold ?? d.NextRunIsOnHold ?? false) as boolean,
    lastRunDate: (d.lastRunDate ?? d.LastRunDate ?? null) as string | null,
    salesRepName: (d.salesRepName ?? d.SalesRepName ?? null) as string | null,
    contacts: contactsRaw.map(convertBlitzContactInfo)
  }
}

export async function getBlitzAdvertisers(search?: string, showAll?: boolean): Promise<BlitzAdvertiserInfo[]> {
  const params: Record<string, string> = {}
  if (search) params.search = search
  if (showAll) params.showAll = 'true'
  const { data } = await api().get<Record<string, unknown>>('/accounts/blitz', { params })
  const raw = (data.advertisers ?? data.Advertisers ?? []) as Record<string, unknown>[]
  return raw.map(convertBlitzAdvertiserInfo)
}

export async function getBlitzOtherAdvertisers(search?: string, salesRepKey?: number, showAll?: boolean): Promise<BlitzAdvertiserInfo[]> {
  const params: Record<string, string> = {}
  if (search) params.search = search
  if (salesRepKey) params.salesRepKey = String(salesRepKey)
  if (showAll) params.showAll = 'true'
  const { data } = await api().get<Record<string, unknown>>('/accounts/other/blitz', { params })
  const raw = (data.advertisers ?? data.Advertisers ?? []) as Record<string, unknown>[]
  return raw.map(convertBlitzAdvertiserInfo)
}

export async function getUnifiedAdvertisers(search?: string, salesRepKey?: number, activeOnly?: boolean, hasBalance?: boolean): Promise<{ advertisers: AdvertiserInfo[], truncated: boolean }> {
  const params: Record<string, string> = {}
  if (search) params.search = search
  if (salesRepKey) params.salesRepKey = String(salesRepKey)
  if (activeOnly) params.activeOnly = 'true'
  if (hasBalance) params.hasBalance = 'true'
  const { data } = await api().get<Record<string, unknown>>('/accounts/unified', { params })
  const raw = (data.advertisers ?? data.Advertisers ?? []) as Record<string, unknown>[]
  const truncated = (data.truncated ?? data.Truncated ?? false) as boolean
  return { advertisers: raw.map(convertAdvertiserInfo), truncated }
}

export async function getUnifiedBlitzAdvertisers(search?: string, salesRepKey?: number, activeOnly?: boolean, hasBalance?: boolean): Promise<BlitzAdvertiserInfo[]> {
  const params: Record<string, string> = {}
  if (search) params.search = search
  if (salesRepKey) params.salesRepKey = String(salesRepKey)
  if (activeOnly) params.activeOnly = 'true'
  if (hasBalance) params.hasBalance = 'true'
  const { data } = await api().get<Record<string, unknown>>('/accounts/unified/blitz', { params })
  const raw = (data.advertisers ?? data.Advertisers ?? []) as Record<string, unknown>[]
  return raw.map(convertBlitzAdvertiserInfo)
}

export async function getBlitzContacts(advertiserKey: number): Promise<BlitzContactInfo[]> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/blitz-contacts`)
  const raw = (data.contacts ?? data.Contacts ?? []) as Record<string, unknown>[]
  return raw.map(convertBlitzContactInfo)
}

export async function getAccessibleSalesReps(): Promise<SalesRepListItem[]> {
  const { data } = await api().get<Record<string, unknown>>('/accounts/other/sales-reps')
  const raw = (data.salesReps ?? data.SalesReps ?? []) as Record<string, unknown>[]
  return raw.map(d => ({
    salesRepKey: (d.salesRepKey ?? d.SalesRepKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string
  }))
}

export async function getAdvertiserDetail(advertiserKey: number): Promise<AdvertiserDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}`)
  const contactsRaw = (data.contacts ?? data.Contacts ?? []) as Record<string, unknown>[]
  const agingRaw = (data.agingBalance ?? data.AgingBalance ?? null) as Record<string, unknown> | null
  return {
    advertiserKey: (data.advertiserKey ?? data.AdvertiserKey ?? 0) as number,
    name: (data.name ?? data.Name ?? '') as string,
    addressee: (data.addressee ?? data.Addressee ?? '') as string,
    email: (data.email ?? data.Email ?? '') as string,
    phone: (data.phone ?? data.Phone ?? '') as string,
    webSite: (data.webSite ?? data.WebSite ?? '') as string,
    address: (data.address ?? data.Address ?? '') as string,
    city: (data.city ?? data.City ?? '') as string,
    state: (data.state ?? data.State ?? '') as string,
    postalCode: (data.postalCode ?? data.PostalCode ?? '') as string,
    accountBalance: (data.accountBalance ?? data.AccountBalance ?? 0) as number,
    primaryContactFKey: (data.primaryContactFKey ?? data.PrimaryContactFKey ?? null) as number | null,
    billingContactFKey: (data.billingContactFKey ?? data.BillingContactFKey ?? null) as number | null,
    agingBalance: convertAgingBalance(agingRaw),
    contacts: contactsRaw.map(convertContactInfo),
    lastTouchDate: (data.lastTouchDate ?? data.LastTouchDate ?? null) as string | null,
    overdueTouchCount: (data.overdueTouchCount ?? data.OverdueTouchCount ?? 0) as number,
    nextTouchDate: (data.nextTouchDate ?? data.NextTouchDate ?? null) as string | null,
    salesRepName: (data.salesRepName ?? data.SalesRepName ?? '') as string,
    advertiserSetName: (data.advertiserSetName ?? data.AdvertiserSetName ?? '') as string,
    billingOfficeName: (data.billingOfficeName ?? data.BillingOfficeName ?? '') as string
  }
}

// ── Account data endpoints ──────────────────────────────────────────────────

function convertOrderInfo(d: Record<string, unknown>): OrderInfo {
  return {
    orderKey: (d.orderKey ?? d.OrderKey ?? 0) as number,
    orderNumber: (d.orderNumber ?? d.OrderNumber ?? '') as string,
    orderType: (d.orderType ?? d.OrderType ?? 'Retail') as 'Retail' | 'Classified',
    isPackage: (d.isPackage ?? d.IsPackage ?? false) as boolean,
    description: (d.description ?? d.Description ?? '') as string,
    startDate: (d.startDate ?? d.StartDate ?? null) as string | null,
    endDate: (d.endDate ?? d.EndDate ?? null) as string | null,
    amount: (d.amount ?? d.Amount ?? 0) as number,
    nextRunDate: (d.nextRunDate ?? d.NextRunDate ?? null) as string | null,
    runsCount: (d.runsCount ?? d.RunsCount ?? 0) as number,
    publications: (d.publications ?? d.Publications ?? '') as string,
    nextRunIsOnHold: (d.nextRunIsOnHold ?? d.NextRunIsOnHold ?? false) as boolean,
    quotedRunsCount: (d.quotedRunsCount ?? d.QuotedRunsCount ?? 0) as number,
    quoteExpirationDate: (d.quoteExpirationDate ?? d.QuoteExpirationDate ?? null) as string | null,
    quoteAmount: (d.quoteAmount ?? d.QuoteAmount ?? 0) as number
  }
}

function convertActivityInfo(d: Record<string, unknown>): ActivityInfo {
  return {
    activityKey: (d.activityKey ?? d.ActivityKey ?? 0) as number,
    startTime: (d.startTime ?? d.StartTime ?? null) as string | null,
    endTime: (d.endTime ?? d.EndTime ?? null) as string | null,
    startTimeHasTimeOfDay: (d.startTimeHasTimeOfDay ?? d.StartTimeHasTimeOfDay ?? false) as boolean,
    endTimeHasTimeOfDay: (d.endTimeHasTimeOfDay ?? d.EndTimeHasTimeOfDay ?? false) as boolean,
    title: (d.title ?? d.Title ?? '') as string,
    contactName: (d.contactName ?? d.ContactName ?? '') as string,
    contactFKey: (d.contactFKey ?? d.ContactFKey ?? null) as number | null,
    activityTypeName: (d.activityTypeName ?? d.ActivityTypeName ?? '') as string,
    activityTypeFKey: (d.activityTypeFKey ?? d.ActivityTypeFKey ?? null) as number | null,
    activityStatusName: (d.activityStatusName ?? d.ActivityStatusName ?? '') as string,
    activityStatusFKey: (d.activityStatusFKey ?? d.ActivityStatusFKey ?? null) as number | null,
    activityPriorityFKey: (d.activityPriorityFKey ?? d.ActivityPriorityFKey ?? null) as number | null,
    activityPriorityName: (d.activityPriorityName ?? d.ActivityPriorityName ?? '') as string,
    notes: (d.notes ?? d.Notes ?? '') as string,
    isClosed: (d.isClosed ?? d.IsClosed ?? false) as boolean,
    assignedToSalesRepKey: (d.assignedToSalesRepKey ?? d.AssignedToSalesRepKey ?? null) as number | null,
    assignedToName: (d.assignedToName ?? d.AssignedToName ?? '') as string,
    dealFKey: (d.dealFKey ?? d.DealFKey ?? null) as number | null,
    dealName: (d.dealName ?? d.DealName ?? '') as string
  }
}

function convertInvoiceInfo(d: Record<string, unknown>): InvoiceInfo {
  return {
    invoiceKey: (d.invoiceKey ?? d.InvoiceKey ?? 0) as number,
    invoiceNumber: (d.invoiceNumber ?? d.InvoiceNumber ?? '') as string,
    date: (d.date ?? d.Date ?? '') as string,
    dueDate: (d.dueDate ?? d.DueDate ?? null) as string | null,
    description: (d.description ?? d.Description ?? '') as string,
    amount: (d.amount ?? d.Amount ?? 0) as number,
    balanceDue: (d.balanceDue ?? d.BalanceDue ?? 0) as number,
    isPastDue: (d.isPastDue ?? d.IsPastDue ?? false) as boolean,
    daysPastDue: (d.daysPastDue ?? d.DaysPastDue ?? 0) as number
  }
}

function convertPaymentInfo(d: Record<string, unknown>): PaymentInfo {
  return {
    paymentKey: (d.paymentKey ?? d.PaymentKey ?? 0) as number,
    date: (d.date ?? d.Date ?? '') as string,
    amount: (d.amount ?? d.Amount ?? 0) as number,
    remainingAmount: (d.remainingAmount ?? d.RemainingAmount ?? 0) as number,
    paymentMethodName: (d.paymentMethodName ?? d.PaymentMethodName ?? '') as string,
    description: (d.description ?? d.Description ?? '') as string,
    checkNumber: (d.checkNumber ?? d.CheckNumber ?? '') as string,
    partialAccountNumber: (d.partialAccountNumber ?? d.PartialAccountNumber ?? '') as string
  }
}

export async function getAccountOrders(advertiserKey: number): Promise<OrderInfo[]> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/orders`)
  const raw = (data.orders ?? data.Orders ?? []) as Record<string, unknown>[]
  return raw.map(convertOrderInfo)
}

export async function getAccountActivities(
  advertiserKey: number,
  options?: {
    maxResults?: number
    pageNumber?: number
    openOnly?: boolean
    searchText?: string
  }
): Promise<{ activities: ActivityInfo[]; totalCount: number }> {
  const params: Record<string, unknown> = {}
  if (options?.maxResults != null) params.maxResults = options.maxResults
  if (options?.pageNumber != null) params.pageNumber = options.pageNumber
  if (options?.openOnly) params.openOnly = true
  if (options?.searchText?.trim()) params.searchText = options.searchText.trim()
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/activities`, { params })
  const raw = (data.activities ?? data.Activities ?? []) as Record<string, unknown>[]
  const totalCount = (data.totalCount ?? data.TotalCount ?? 0) as number
  return { activities: raw.map(convertActivityInfo), totalCount }
}

export async function getAccountInvoices(advertiserKey: number): Promise<{ invoices: InvoiceInfo[]; totalOutstanding: number }> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/invoices`)
  const raw = (data.invoices ?? data.Invoices ?? []) as Record<string, unknown>[]
  return {
    invoices: raw.map(convertInvoiceInfo),
    totalOutstanding: (data.totalOutstanding ?? data.TotalOutstanding ?? 0) as number
  }
}

export async function getAccountPayments(advertiserKey: number): Promise<PaymentInfo[]> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/payments`)
  const raw = (data.payments ?? data.Payments ?? []) as Record<string, unknown>[]
  return raw.map(convertPaymentInfo)
}

export async function getAdvertiserDeals(advertiserKey: number): Promise<DealCardInfo[]> {
  const { data } = await api().get<Record<string, unknown>[]>(`/accounts/${advertiserKey}/deals`)
  return (data as Record<string, unknown>[]).map(convertDealCardInfo)
}

// ── Contact CRUD endpoints ──────────────────────────────────────────────────

export async function createContact(advertiserKey: number, request: SaveContactRequest): Promise<SaveContactResponse> {
  const { data } = await api().post<Record<string, unknown>>(`/accounts/${advertiserKey}/contacts`, request)
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    contactKey: (data.contactKey ?? data.ContactKey ?? 0) as number
  }
}

export async function updateContact(advertiserKey: number, contactKey: number, request: SaveContactRequest): Promise<SaveContactResponse> {
  const { data } = await api().put<Record<string, unknown>>(`/accounts/${advertiserKey}/contacts/${contactKey}`, request)
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    contactKey: (data.contactKey ?? data.ContactKey ?? 0) as number
  }
}

export async function deleteContact(advertiserKey: number, contactKey: number): Promise<DeleteContactResponse> {
  const { data } = await api().delete<Record<string, unknown>>(`/accounts/${advertiserKey}/contacts/${contactKey}`)
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

export async function setContactInactive(advertiserKey: number, contactKey: number, request: SetContactInactiveRequest): Promise<SetContactInactiveResponse> {
  const { data } = await api().put<Record<string, unknown>>(`/accounts/${advertiserKey}/contacts/${contactKey}/inactive`, request)
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

export async function updateContactAssignments(advertiserKey: number, request: UpdateContactAssignmentsRequest): Promise<{ success: boolean; message: string }> {
  const { data } = await api().put<Record<string, unknown>>(`/accounts/${advertiserKey}/contact-assignments`, request)
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

// ── Activity lookup & editing endpoints ──────────────────────────────────

function convertLookupItem(d: Record<string, unknown>): ActivityLookupItem {
  return {
    key: (d.key ?? d.Key ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string
  }
}

export async function getActivityLookups(): Promise<ActivityLookupsResponse> {
  const { data } = await api().get<Record<string, unknown>>('/activity-lookups')
  const typesRaw = (data.types ?? data.Types ?? []) as Record<string, unknown>[]
  const statusesRaw = (data.statuses ?? data.Statuses ?? []) as Record<string, unknown>[]
  const prioritiesRaw = (data.priorities ?? data.Priorities ?? []) as Record<string, unknown>[]
  const byName = (a: ActivityLookupItem, b: ActivityLookupItem) => a.name.localeCompare(b.name)
  return {
    types: typesRaw.map(convertLookupItem).sort(byName),
    statuses: statusesRaw.map(convertLookupItem).sort(byName),
    priorities: prioritiesRaw.map(convertLookupItem).sort(byName)
  }
}

export async function updateActivity(activityKey: number, request: SaveActivityRequest): Promise<SaveActivityResponse> {
  const { data } = await api().put<Record<string, unknown>>(`/activities/${activityKey}`, {
    Title: request.title,
    StartTime: request.startTime,
    EndTime: request.endTime,
    StartTimeHasTimeOfDay: request.startTimeHasTimeOfDay,
    EndTimeHasTimeOfDay: request.endTimeHasTimeOfDay,
    ActivityTypeFKey: request.activityTypeFKey,
    ActivityStatusFKey: request.activityStatusFKey,
    ActivityPriorityFKey: request.activityPriorityFKey,
    ContactFKey: request.contactFKey,
    Notes: request.notes,
    AssignedToSalesRepKey: request.assignedToSalesRepKey ?? null,
    DealFKey: request.dealFKey ?? null
  })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

export async function getActivityDefaults(): Promise<ActivityDefaultsResponse> {
  const { data } = await api().get<Record<string, unknown>>('/activity-defaults')
  return {
    quickNoteTypeFKey: (data.quickNoteTypeFKey ?? data.QuickNoteTypeFKey ?? null) as number | null,
    quickNoteStatusFKey: (data.quickNoteStatusFKey ?? data.QuickNoteStatusFKey ?? null) as number | null,
    quickNotePriorityFKey: (data.quickNotePriorityFKey ?? data.QuickNotePriorityFKey ?? null) as number | null,
    followUpTypeFKey: (data.followUpTypeFKey ?? data.FollowUpTypeFKey ?? null) as number | null,
    followUpStatusFKey: (data.followUpStatusFKey ?? data.FollowUpStatusFKey ?? null) as number | null,
    followUpPriorityFKey: (data.followUpPriorityFKey ?? data.FollowUpPriorityFKey ?? null) as number | null,
    followUpRegardingDefault: (data.followUpRegardingDefault ?? data.FollowUpRegardingDefault ?? 'Follow Up') as string
  }
}

export async function createActivity(advertiserKey: number, request: CreateActivityRequest): Promise<SaveActivityResponse> {
  const { data } = await api().post<Record<string, unknown>>(`/accounts/${advertiserKey}/activities`, {
    ...request,
    AssignedToSalesRepKey: request.assignedToSalesRepKey ?? null
  })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    activityKey: (data.activityKey ?? data.ActivityKey ?? 0) as number,
    title: (data.title ?? data.Title) as string | undefined
  }
}

export async function getSalesReps(): Promise<SalesRepListItem[]> {
  const { data } = await api().get<Record<string, unknown>>('/sales-reps')
  const raw = (data.salesReps ?? data.SalesReps ?? []) as Record<string, unknown>[]
  return raw.map(d => ({
    salesRepKey: (d.salesRepKey ?? d.SalesRepKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string
  })).sort((a, b) => a.name.localeCompare(b.name))
}

export async function getContactActivities(advertiserKey: number, contactKey: number): Promise<ActivityInfo[]> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/contacts/${contactKey}/activities`)
  const raw = (data.activities ?? data.Activities ?? []) as Record<string, unknown>[]
  return raw.map(convertActivityInfo)
}

// ── Contact roles endpoint ──────────────────────────────────────────────────

function convertContactRoleInfo(d: Record<string, unknown>): ContactRoleInfo {
  return {
    key: (d.key ?? d.Key ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string
  }
}

export async function getContactRoles(): Promise<ContactRolesResponse> {
  const { data } = await api().get<Record<string, unknown>>('/contact-roles')
  const raw = (data.roles ?? data.Roles ?? []) as Record<string, unknown>[]
  return {
    roles: raw.map(convertContactRoleInfo)
  }
}

// ── Invoice & Payment detail endpoints ───────────────────────────────────────

function convertBillableItemInfo(d: Record<string, unknown>): BillableItemInfo {
  return {
    date: (d.date ?? d.Date ?? null) as string | null,
    product: (d.product ?? d.Product ?? '') as string,
    orderNumber: (d.orderNumber ?? d.OrderNumber ?? '') as string,
    description: (d.description ?? d.Description ?? '') as string,
    publicationName: (d.publicationName ?? d.PublicationName ?? '') as string,
    salesTypeName: (d.salesTypeName ?? d.SalesTypeName ?? '') as string,
    amount: (d.amount ?? d.Amount ?? 0) as number
  }
}

function convertPaymentAllocationInfo(d: Record<string, unknown>): PaymentAllocationInfo {
  return {
    debitKey: (d.debitKey ?? d.DebitKey ?? 0) as number,
    debitType: (d.debitType ?? d.DebitType ?? '') as string,
    reference: (d.reference ?? d.Reference ?? '') as string,
    date: (d.date ?? d.Date ?? null) as string | null,
    description: (d.description ?? d.Description ?? '') as string,
    amountApplied: (d.amountApplied ?? d.AmountApplied ?? 0) as number
  }
}

export async function getInvoiceDetail(advertiserKey: number, invoiceKey: number): Promise<InvoiceDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/invoices/${invoiceKey}`)
  const itemsRaw = (data.billableItems ?? data.BillableItems ?? []) as Record<string, unknown>[]
  return {
    invoiceKey: (data.invoiceKey ?? data.InvoiceKey ?? 0) as number,
    invoiceNumber: (data.invoiceNumber ?? data.InvoiceNumber ?? '') as string,
    date: (data.date ?? data.Date ?? null) as string | null,
    dueDate: (data.dueDate ?? data.DueDate ?? null) as string | null,
    description: (data.description ?? data.Description ?? '') as string,
    amount: (data.amount ?? data.Amount ?? 0) as number,
    balanceDue: (data.balanceDue ?? data.BalanceDue ?? 0) as number,
    amountPaid: (data.amountPaid ?? data.AmountPaid ?? 0) as number,
    isPastDue: (data.isPastDue ?? data.IsPastDue ?? false) as boolean,
    daysPastDue: (data.daysPastDue ?? data.DaysPastDue ?? 0) as number,
    billableItems: itemsRaw.map(convertBillableItemInfo)
  }
}

export async function getPaymentDetail(advertiserKey: number, paymentKey: number): Promise<PaymentDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/payments/${paymentKey}`)
  const allocRaw = (data.allocations ?? data.Allocations ?? []) as Record<string, unknown>[]
  return {
    paymentKey: (data.paymentKey ?? data.PaymentKey ?? 0) as number,
    date: (data.date ?? data.Date ?? null) as string | null,
    amount: (data.amount ?? data.Amount ?? 0) as number,
    paymentMethodName: (data.paymentMethodName ?? data.PaymentMethodName ?? '') as string,
    description: (data.description ?? data.Description ?? '') as string,
    checkNumber: (data.checkNumber ?? data.CheckNumber ?? '') as string,
    partialAccountNumber: (data.partialAccountNumber ?? data.PartialAccountNumber ?? '') as string,
    allocations: allocRaw.map(convertPaymentAllocationInfo)
  }
}

// ── PDF download endpoints ───────────────────────────────────────────────────

export async function downloadInvoicePdf(advertiserKey: number, invoiceKey: number): Promise<void> {
  const response = await api().get(`/accounts/${advertiserKey}/invoices/${invoiceKey}/pdf`, {
    responseType: 'blob'
  })
  const blob = new Blob([response.data], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `Invoice-${invoiceKey}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function downloadPaymentReceipt(advertiserKey: number, paymentKey: number): Promise<void> {
  const response = await api().get(`/accounts/${advertiserKey}/payments/${paymentKey}/receipt`, {
    responseType: 'blob'
  })
  const blob = new Blob([response.data], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `PaymentReceipt-${paymentKey}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ============================================================================
// Retail Order Detail endpoints
// ============================================================================

function convertRetailRunInfo(d: Record<string, unknown>): RetailRunInfo {
  return {
    runKey: (d.runKey ?? d.RunKey ?? 0) as number,
    date: (d.date ?? d.Date ?? null) as string | null,
    publicationName: (d.publicationName ?? d.PublicationName ?? '') as string,
    publicationAbbreviation: (d.publicationAbbreviation ?? d.PublicationAbbreviation ?? '') as string,
    publicationFKey: (d.publicationFKey ?? d.PublicationFKey ?? null) as number | null,
    imageNumber: (d.imageNumber ?? d.ImageNumber ?? 1) as number,
    amount: (d.amount ?? d.Amount ?? 0) as number,
    description: (d.description ?? d.Description ?? '') as string,
    adSize: (d.adSize ?? d.AdSize ?? '') as string,
    color: (d.color ?? d.Color ?? '') as string,
    isOnHold: (d.isOnHold ?? d.IsOnHold ?? false) as boolean,
    holdReasonName: (d.holdReasonName ?? d.HoldReasonName ?? '') as string,
    isCancelled: (d.isCancelled ?? d.IsCancelled ?? false) as boolean,
    cancelReasonName: (d.cancelReasonName ?? d.CancelReasonName ?? '') as string,
    isQuote: (d.isQuote ?? d.IsQuote ?? false) as boolean,
    isInvoiced: (d.isInvoiced ?? d.IsInvoiced ?? false) as boolean,
    canModify: (d.canModify ?? d.CanModify ?? false) as boolean,
    isIssueClosed: (d.isIssueClosed ?? d.IsIssueClosed ?? false) as boolean,
    canUncancel: (d.canUncancel ?? d.CanUncancel ?? false) as boolean
  }
}

function convertOrderPublication(d: Record<string, unknown>): OrderPublicationInfo {
  return {
    publicationKey: (d.publicationKey ?? d.PublicationKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    abbreviation: (d.abbreviation ?? d.Abbreviation ?? '') as string
  }
}

function convertOrderImageInfo(d: Record<string, unknown>): OrderImageInfo {
  return {
    proofKey: (d.proofKey ?? d.ProofKey ?? 0) as number,
    imageNumber: (d.imageNumber ?? d.ImageNumber ?? 0) as number,
    proofImageUrl: (d.proofImageUrl ?? d.ProofImageUrl ?? '') as string,
    isPdf: (d.isPdf ?? d.IsPdf ?? false) as boolean,
    statusDisplay: (d.statusDisplay ?? d.StatusDisplay ?? '') as string,
    isFinalized: (d.isFinalized ?? d.IsFinalized ?? false) as boolean
  }
}

function convertRetailOrderDetail(data: Record<string, unknown>): RetailOrderDetailResponse {
  const runsRaw = (data.runs ?? data.Runs ?? []) as Record<string, unknown>[]
  const pubsRaw = (data.publications ?? data.Publications ?? []) as Record<string, unknown>[]
  const imgsRaw = (data.images ?? data.Images ?? []) as Record<string, unknown>[]
  return {
    isPackage: (data.isPackage ?? data.IsPackage ?? false) as boolean,
    orderKey: (data.orderKey ?? data.OrderKey ?? 0) as number,
    orderNumber: (data.orderNumber ?? data.OrderNumber ?? '') as string,
    advertiserName: (data.advertiserName ?? data.AdvertiserName ?? '') as string,
    description: (data.description ?? data.Description ?? '') as string,
    adSize: (data.adSize ?? data.AdSize ?? '') as string,
    color: (data.color ?? data.Color ?? '') as string,
    salesTypeName: (data.salesTypeName ?? data.SalesTypeName ?? '') as string,
    rateName: (data.rateName ?? data.RateName ?? '') as string,
    maxImageNumber: (data.maxImageNumber ?? data.MaxImageNumber ?? 1) as number,
    creditLimit: (data.creditLimit ?? data.CreditLimit ?? 0) as number,
    creditExtended: (data.creditExtended ?? data.CreditExtended ?? 0) as number,
    creditLimitExceeded: (data.creditLimitExceeded ?? data.CreditLimitExceeded ?? false) as boolean,
    runs: runsRaw.map(convertRetailRunInfo),
    publications: pubsRaw.map(convertOrderPublication),
    images: imgsRaw.map(convertOrderImageInfo),
    quoteExpirationDate: (data.quoteExpirationDate ?? data.QuoteExpirationDate ?? null) as string | null,
    contactFKey: (data.contactFKey ?? data.ContactFKey ?? null) as number | null,
    hasQuotedRuns: (data.hasQuotedRuns ?? data.HasQuotedRuns ?? false) as boolean
  }
}

function convertOrderActionResponse(data: Record<string, unknown>): OrderActionResponse {
  const updatedRaw = (data.updatedOrder ?? data.UpdatedOrder ?? null) as Record<string, unknown> | null
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    updatedOrder: updatedRaw ? convertRetailOrderDetail(updatedRaw) : undefined
  }
}

function convertReasonInfo(d: Record<string, unknown>): ReasonInfo {
  return {
    key: (d.key ?? d.Key ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string
  }
}

export async function getRetailOrderDetail(orderKey: number): Promise<RetailOrderDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/orders/${orderKey}`)
  return convertRetailOrderDetail(data)
}

export async function getOrderImages(orderKey: number): Promise<OrderImageInfo[]> {
  const { data } = await api().get<Record<string, unknown>[]>(`/orders/${orderKey}/images`)
  return (data ?? []).map(d => convertOrderImageInfo(d as Record<string, unknown>))
}

export async function getAdRunSummary(advertiserKey: number): Promise<AdRunSummaryResponse | null> {
  try {
    const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/ad-run-summary`)
    return {
      lastRunDate: (data.lastRunDate ?? data.LastRunDate ?? null) as string | null,
      lastRunOrderKey: (data.lastRunOrderKey ?? data.LastRunOrderKey ?? null) as number | null,
      lastRunImageNumber: (data.lastRunImageNumber ?? data.LastRunImageNumber ?? 1) as number,
      lastRunPublication: (data.lastRunPublication ?? data.LastRunPublication ?? '') as string,
      lastRunAdSize: (data.lastRunAdSize ?? data.LastRunAdSize ?? '') as string,
      lastRunAmount: (data.lastRunAmount ?? data.LastRunAmount ?? 0) as number,
      lastRunIsOnHold: (data.lastRunIsOnHold ?? data.LastRunIsOnHold ?? false) as boolean,
      nextRunDate: (data.nextRunDate ?? data.NextRunDate ?? null) as string | null,
      nextRunOrderKey: (data.nextRunOrderKey ?? data.NextRunOrderKey ?? null) as number | null,
      nextRunImageNumber: (data.nextRunImageNumber ?? data.NextRunImageNumber ?? 1) as number,
      nextRunPublication: (data.nextRunPublication ?? data.NextRunPublication ?? '') as string,
      nextRunAdSize: (data.nextRunAdSize ?? data.NextRunAdSize ?? '') as string,
      nextRunAmount: (data.nextRunAmount ?? data.NextRunAmount ?? 0) as number,
      nextRunIsOnHold: (data.nextRunIsOnHold ?? data.NextRunIsOnHold ?? false) as boolean,
      futureRunCount: (data.futureRunCount ?? data.FutureRunCount ?? 0) as number,
    }
  } catch {
    return null
  }
}

export async function getOrderLookups(): Promise<OrderLookupsResponse> {
  const { data } = await api().get<Record<string, unknown>>('/orders/lookups')
  const holdRaw = (data.holdReasons ?? data.HoldReasons ?? []) as Record<string, unknown>[]
  const cancelRaw = (data.cancelReasons ?? data.CancelReasons ?? []) as Record<string, unknown>[]
  return {
    holdReasons: holdRaw.map(convertReasonInfo),
    cancelReasons: cancelRaw.map(convertReasonInfo)
  }
}

export async function scheduleRuns(orderKey: number, publicationKey: number, startDate: string, numberOfRuns: number, imageNumber?: number | null): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/schedule-runs', {
    OrderKey: orderKey,
    PublicationKey: publicationKey,
    StartDate: startDate,
    NumberOfRuns: numberOfRuns,
    ImageNumber: imageNumber ?? null
  })
  return convertOrderActionResponse(data)
}

export async function scheduleFromTemplate(
  orderKey: number, templateRunKeys: number[], startDate: string, numberOfRuns: number,
  startingImageNumber: number | null, imageChangeMode: string
): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/schedule-from-template', {
    OrderKey: orderKey,
    TemplateRunKeys: templateRunKeys,
    StartDate: startDate,
    NumberOfRuns: numberOfRuns,
    StartingImageNumber: startingImageNumber,
    ImageChangeMode: imageChangeMode
  }, { timeout: 120000 })
  return convertOrderActionResponse(data)
}

export async function cancelRuns(orderKey: number, runKeys: number[], cancelReasonFKey: number): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/cancel-runs', {
    OrderKey: orderKey,
    RunKeys: runKeys,
    CancelReasonFKey: cancelReasonFKey
  })
  return convertOrderActionResponse(data)
}

export async function holdRuns(orderKey: number, runKeys: number[], holdReasonFKey?: number | null): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/hold-runs', {
    OrderKey: orderKey,
    RunKeys: runKeys,
    HoldReasonFKey: holdReasonFKey ?? null
  })
  return convertOrderActionResponse(data)
}

export async function releaseHold(orderKey: number, runKeys: number[]): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/release-hold', {
    OrderKey: orderKey,
    RunKeys: runKeys
  })
  return convertOrderActionResponse(data)
}

export async function uncancelRuns(orderKey: number, runKeys: number[]): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/uncancel-runs', {
    OrderKey: orderKey,
    RunKeys: runKeys
  })
  return convertOrderActionResponse(data)
}

export async function convertQuoteToOrder(orderKey: number, runKeys: number[]): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/convert-quote', {
    OrderKey: orderKey,
    RunKeys: runKeys
  })
  return convertOrderActionResponse(data)
}

export async function addToQuote(orderKey: number, runKeys: number[], quoteExpirationDate?: string | null): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/add-to-quote', {
    OrderKey: orderKey,
    RunKeys: runKeys,
    QuoteExpirationDate: quoteExpirationDate || null
  })
  return convertOrderActionResponse(data)
}

export async function incrementImageNumber(orderKey: number): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/increment-image', {
    OrderKey: orderKey
  })
  return convertOrderActionResponse(data)
}

export async function updateRunImageNumber(orderKey: number, runKey: number, imageNumber: number): Promise<OrderActionResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/update-run-image', {
    OrderKey: orderKey,
    RunKey: runKey,
    ImageNumber: imageNumber
  })
  return convertOrderActionResponse(data)
}

// ============================================================================
// Email Templates
// ============================================================================

import type {
  EmailTemplateInfo,
  EmailTemplatesResponse,
  EmailTemplatePreviewResponse,
  SendActivityEmailRequest,
  SendActivityEmailResponse,
  SendAdvertiserEmailRequest
} from '@/types'

export async function getEmailTemplates(opts?: { includeAdOrderTemplates?: boolean }): Promise<EmailTemplatesResponse> {
  const params: Record<string, unknown> = {}
  if (opts?.includeAdOrderTemplates) params.includeAdOrderTemplates = true
  const { data } = await api().get<Record<string, unknown>>('/email-templates', { params })
  const raw = (data.templates ?? data.Templates ?? []) as Record<string, unknown>[]
  return {
    templates: raw.map(t => ({
      key: (t.Key ?? t.key) as number,
      name: ((t.Name ?? t.name) as string) ?? '',
      description: ((t.Description ?? t.description) as string) ?? '',
      templateType: (t.TemplateType ?? t.templateType) as number,
      emailSubjectLine: ((t.EmailSubjectLine ?? t.emailSubjectLine) as string) ?? ''
    } as EmailTemplateInfo))
  }
}

export async function getEmailTemplatePreview(
  templateKey: number,
  advertiserKey: number,
  contactKey?: number | null,
  orderKey?: number | null
): Promise<EmailTemplatePreviewResponse> {
  const params: Record<string, unknown> = { advertiserKey }
  if (contactKey) params.contactKey = contactKey
  if (orderKey) params.orderKey = orderKey
  const { data } = await api().get<Record<string, unknown>>(
    `/email-templates/${templateKey}/preview`,
    { params }
  )
  return {
    success: (data.Success ?? data.success) as boolean,
    message: ((data.Message ?? data.message) as string) ?? '',
    key: (data.Key ?? data.key) as number,
    name: ((data.Name ?? data.name) as string) ?? '',
    emailSubjectLine: ((data.EmailSubjectLine ?? data.emailSubjectLine) as string) ?? '',
    documentHtml: ((data.DocumentHtml ?? data.documentHtml) as string) ?? ''
  }
}

export async function getMySignatureHtml(): Promise<string> {
  const { data } = await api().get<Record<string, unknown>>('/my-signature-html')
  return ((data.SignatureHtml ?? data.signatureHtml) as string) ?? ''
}

export async function sendAdvertiserEmail(
  advertiserKey: number,
  request: SendAdvertiserEmailRequest
): Promise<SendActivityEmailResponse> {
  const { data } = await api().post<Record<string, unknown>>(
    `/accounts/${advertiserKey}/send-email`,
    {
      Subject: request.subject,
      HtmlBody: request.htmlBody,
      ToEmail: request.toEmail,
      ToName: request.toName,
      CcEmails: request.ccEmails || null,
      BccEmails: request.bccEmails || null,
      TemplateKey: request.templateKey ?? null,
      ContactKey: request.contactKey ?? null,
      InvoiceKey: request.invoiceKey ?? null,
      PaymentKey: request.paymentKey ?? null,
      OrderKey: request.orderKey ?? null,
      OrderReportType: request.orderReportType ?? null
    }
  )
  return {
    success: (data.Success ?? data.success) as boolean,
    message: ((data.Message ?? data.message) as string) ?? ''
  }
}

export async function downloadOrderReportPdf(orderKey: number, reportType: string): Promise<void> {
  const response = await api().get(`/orders/${orderKey}/report-pdf?reportType=${reportType}`, {
    responseType: 'blob'
  })
  const blob = new Blob([response.data], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${reportType}-${orderKey}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function previewOrderReportPdf(orderKey: number, reportType: string): Promise<string> {
  const response = await api().get(`/orders/${orderKey}/report-pdf?reportType=${reportType}`, {
    responseType: 'blob'
  })
  const blob = new Blob([response.data], { type: 'application/pdf' })
  return URL.createObjectURL(blob)
}

export async function previewInvoicePdf(advertiserKey: number, invoiceKey: number): Promise<string> {
  const response = await api().get(`/accounts/${advertiserKey}/invoices/${invoiceKey}/pdf`, {
    responseType: 'blob'
  })
  const blob = new Blob([response.data], { type: 'application/pdf' })
  return URL.createObjectURL(blob)
}

export async function previewPaymentReceiptPdf(advertiserKey: number, paymentKey: number): Promise<string> {
  const response = await api().get(`/accounts/${advertiserKey}/payments/${paymentKey}/receipt`, {
    responseType: 'blob'
  })
  const blob = new Blob([response.data], { type: 'application/pdf' })
  return URL.createObjectURL(blob)
}

export async function sendActivityEmail(
  activityKey: number,
  request: SendActivityEmailRequest
): Promise<SendActivityEmailResponse> {
  const { data } = await api().post<Record<string, unknown>>(
    `/activities/${activityKey}/send-email`,
    {
      Subject: request.subject,
      HtmlBody: request.htmlBody,
      ToEmail: request.toEmail,
      ToName: request.toName,
      CcEmails: request.ccEmails || null,
      BccEmails: request.bccEmails || null,
      TemplateKey: request.templateKey ?? null,
      ContactKey: request.contactKey ?? null
    }
  )
  return {
    success: (data.Success ?? data.success) as boolean,
    message: ((data.Message ?? data.message) as string) ?? ''
  }
}

// ============================================================================
// Deals
// ============================================================================

function convertDealPipelineInfo(d: Record<string, unknown>): DealPipelineInfo {
  return {
    dealPipelineKey: (d.dealPipelineKey ?? d.DealPipelineKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string
  }
}

function convertDealStageInfo(d: Record<string, unknown>): DealStageInfo {
  return {
    dealStageKey: (d.dealStageKey ?? d.DealStageKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    stageNumber: (d.stageNumber ?? d.StageNumber ?? null) as number | null,
    probability: (d.probability ?? d.Probability ?? null) as number | null,
    status: (d.status ?? d.Status ?? '') as string,
    forecastCategory: (d.forecastCategory ?? d.ForecastCategory ?? '') as string,
    dealPipelineFKey: (d.dealPipelineFKey ?? d.DealPipelineFKey ?? null) as number | null,
    dealPipelineName: (d.dealPipelineName ?? d.DealPipelineName ?? '') as string
  }
}

function convertDealLookupItem(d: Record<string, unknown>): LookupItem {
  return {
    key: (d.key ?? d.Key ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string
  }
}

function convertDealCardInfo(d: Record<string, unknown>): DealCardInfo {
  return {
    dealKey: (d.dealKey ?? d.DealKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    amount: (d.amount ?? d.Amount ?? null) as number | null,
    probability: (d.probability ?? d.Probability ?? null) as number | null,
    probableAmount: (d.probableAmount ?? d.ProbableAmount ?? null) as number | null,
    closeDate: (d.closeDate ?? d.CloseDate ?? null) as string | null,
    advertiserName: (d.advertiserName ?? d.AdvertiserName ?? '') as string,
    contactName: (d.contactName ?? d.ContactName ?? '') as string,
    dealTypeName: (d.dealTypeName ?? d.DealTypeName ?? '') as string,
    dealStageFKey: (d.dealStageFKey ?? d.DealStageFKey ?? null) as number | null,
    dealStageName: (d.dealStageName ?? d.DealStageName ?? '') as string,
    stageStatus: (d.stageStatus ?? d.StageStatus ?? '') as string,
    activityCount: (d.activityCount ?? d.ActivityCount ?? 0) as number,
    documentCount: (d.documentCount ?? d.DocumentCount ?? 0) as number
  }
}

function convertDealBoardStageColumn(d: Record<string, unknown>): DealBoardStageColumn {
  const stageRaw = (d.stage ?? d.Stage ?? {}) as Record<string, unknown>
  const dealsRaw = (d.deals ?? d.Deals ?? []) as Record<string, unknown>[]
  return {
    stage: convertDealStageInfo(stageRaw),
    deals: dealsRaw.map(convertDealCardInfo),
    dealCount: (d.dealCount ?? d.DealCount ?? 0) as number,
    stageAmount: (d.stageAmount ?? d.StageAmount ?? 0) as number,
    stageProbableAmount: (d.stageProbableAmount ?? d.StageProbableAmount ?? 0) as number
  }
}

function convertDealBoardResponse(data: Record<string, unknown>): DealBoardResponse {
  const pipelineRaw = (data.pipeline ?? data.Pipeline ?? {}) as Record<string, unknown>
  const availRaw = (data.availablePipelines ?? data.AvailablePipelines ?? []) as Record<string, unknown>[]
  const stagesRaw = (data.stages ?? data.Stages ?? []) as Record<string, unknown>[]
  return {
    pipeline: convertDealPipelineInfo(pipelineRaw),
    availablePipelines: availRaw.map(convertDealPipelineInfo),
    stages: stagesRaw.map(convertDealBoardStageColumn),
    showAllPipelines: (data.showAllPipelines ?? data.ShowAllPipelines ?? false) as boolean,
    totalDealCount: (data.totalDealCount ?? data.TotalDealCount ?? 0) as number,
    totalAmount: (data.totalAmount ?? data.TotalAmount ?? 0) as number,
    totalProbableAmount: (data.totalProbableAmount ?? data.TotalProbableAmount ?? 0) as number
  }
}

function convertDealDetailResponse(data: Record<string, unknown>): DealDetailResponse {
  const activitiesRaw = (data.activities ?? data.Activities ?? []) as Record<string, unknown>[]
  return {
    dealKey: (data.dealKey ?? data.DealKey ?? 0) as number,
    name: (data.name ?? data.Name ?? '') as string,
    amount: (data.amount ?? data.Amount ?? null) as number | null,
    probability: (data.probability ?? data.Probability ?? null) as number | null,
    probableAmount: (data.probableAmount ?? data.ProbableAmount ?? null) as number | null,
    closeDate: (data.closeDate ?? data.CloseDate ?? null) as string | null,
    description: (data.description ?? data.Description ?? '') as string,
    notes: (data.notes ?? data.Notes ?? '') as string,
    advertiserFKey: (data.advertiserFKey ?? data.AdvertiserFKey ?? null) as number | null,
    advertiserName: (data.advertiserName ?? data.AdvertiserName ?? '') as string,
    contactFKey: (data.contactFKey ?? data.ContactFKey ?? null) as number | null,
    contactName: (data.contactName ?? data.ContactName ?? '') as string,
    dealPipelineFKey: (data.dealPipelineFKey ?? data.DealPipelineFKey ?? null) as number | null,
    dealPipelineName: (data.dealPipelineName ?? data.DealPipelineName ?? '') as string,
    dealStageFKey: (data.dealStageFKey ?? data.DealStageFKey ?? null) as number | null,
    dealStageName: (data.dealStageName ?? data.DealStageName ?? '') as string,
    stageStatus: (data.stageStatus ?? data.StageStatus ?? '') as string,
    dealTypeFKey: (data.dealTypeFKey ?? data.DealTypeFKey ?? null) as number | null,
    dealTypeName: (data.dealTypeName ?? data.DealTypeName ?? '') as string,
    dealLeadSourceFKey: (data.dealLeadSourceFKey ?? data.DealLeadSourceFKey ?? null) as number | null,
    dealLeadSourceName: (data.dealLeadSourceName ?? data.DealLeadSourceName ?? '') as string,
    dealLostReasonFKey: (data.dealLostReasonFKey ?? data.DealLostReasonFKey ?? null) as number | null,
    dealLostReasonName: (data.dealLostReasonName ?? data.DealLostReasonName ?? '') as string,
    salesRepFKey: (data.salesRepFKey ?? data.SalesRepFKey ?? null) as number | null,
    salesRepName: (data.salesRepName ?? data.SalesRepName ?? '') as string,
    activities: activitiesRaw.map(convertActivityInfo),
    createdDate: (data.createdDate ?? data.CreatedDate ?? null) as string | null,
    modifiedDate: (data.modifiedDate ?? data.ModifiedDate ?? null) as string | null
  }
}

export async function getDealBoard(params: {
  pipelineKey?: number
  showAllPipelines?: boolean
  period?: string
  customStart?: string
  customEnd?: string
} = {}): Promise<DealBoardResponse> {
  const query = new URLSearchParams()
  if (params.pipelineKey) query.set('pipelineKey', params.pipelineKey.toString())
  if (params.showAllPipelines) query.set('showAllPipelines', 'true')
  if (params.period) query.set('period', params.period)
  if (params.customStart) query.set('customStart', params.customStart)
  if (params.customEnd) query.set('customEnd', params.customEnd)
  const qs = query.toString()
  const { data } = await api().get<Record<string, unknown>>(`/deals/board${qs ? '?' + qs : ''}`)
  return convertDealBoardResponse(data)
}

export async function getDealLookups(): Promise<DealLookupsResponse> {
  const { data } = await api().get<Record<string, unknown>>('/deals/lookups')
  const pipelinesRaw = (data.pipelines ?? data.Pipelines ?? []) as Record<string, unknown>[]
  const stagesRaw = (data.stages ?? data.Stages ?? []) as Record<string, unknown>[]
  const typesRaw = (data.types ?? data.Types ?? []) as Record<string, unknown>[]
  const leadSourcesRaw = (data.leadSources ?? data.LeadSources ?? []) as Record<string, unknown>[]
  const lostReasonsRaw = (data.lostReasons ?? data.LostReasons ?? []) as Record<string, unknown>[]
  return {
    pipelines: pipelinesRaw.map(convertDealPipelineInfo),
    stages: stagesRaw.map(convertDealStageInfo),
    types: typesRaw.map(convertDealLookupItem),
    leadSources: leadSourcesRaw.map(convertDealLookupItem),
    lostReasons: lostReasonsRaw.map(convertDealLookupItem)
  }
}

export async function getDealDetail(dealKey: number): Promise<DealDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/deals/${dealKey}`)
  return convertDealDetailResponse(data)
}

export async function createDeal(request: SaveDealRequest): Promise<SaveDealResponse> {
  const { data } = await api().post<Record<string, unknown>>('/deals', {
    Name: request.name,
    Amount: request.amount,
    Probability: request.probability,
    CloseDate: request.closeDate,
    Description: request.description,
    Notes: request.notes,
    AdvertiserFKey: request.advertiserFKey,
    ContactFKey: request.contactFKey,
    DealPipelineFKey: request.dealPipelineFKey,
    DealStageFKey: request.dealStageFKey,
    DealTypeFKey: request.dealTypeFKey,
    DealLeadSourceFKey: request.dealLeadSourceFKey,
    DealLostReasonFKey: request.dealLostReasonFKey
  })
  return {
    success: (data.Success ?? data.success) as boolean,
    message: ((data.Message ?? data.message) as string) ?? '',
    dealKey: (data.DealKey ?? data.dealKey ?? 0) as number
  }
}

export async function updateDeal(dealKey: number, request: SaveDealRequest): Promise<SaveDealResponse> {
  const { data } = await api().put<Record<string, unknown>>(`/deals/${dealKey}`, {
    Name: request.name,
    Amount: request.amount,
    Probability: request.probability,
    CloseDate: request.closeDate,
    Description: request.description,
    Notes: request.notes,
    AdvertiserFKey: request.advertiserFKey,
    ContactFKey: request.contactFKey,
    DealPipelineFKey: request.dealPipelineFKey,
    DealStageFKey: request.dealStageFKey,
    DealTypeFKey: request.dealTypeFKey,
    DealLeadSourceFKey: request.dealLeadSourceFKey,
    DealLostReasonFKey: request.dealLostReasonFKey
  })
  return {
    success: (data.Success ?? data.success) as boolean,
    message: ((data.Message ?? data.message) as string) ?? '',
    dealKey: (data.DealKey ?? data.dealKey ?? 0) as number
  }
}

export async function moveDealStage(dealKey: number, request: MoveDealRequest): Promise<MoveDealResponse> {
  const { data } = await api().post<Record<string, unknown>>(`/deals/${dealKey}/move`, {
    DealStageFKey: request.dealStageFKey,
    Probability: request.probability
  })
  return {
    success: (data.Success ?? data.success) as boolean,
    message: ((data.Message ?? data.message) as string) ?? ''
  }
}

function convertDealDocumentInfo(d: Record<string, unknown>): DealDocumentInfo {
  return {
    documentKey: (d.documentKey ?? d.DocumentKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    description: (d.description ?? d.Description ?? '') as string,
    filename: (d.filename ?? d.Filename ?? '') as string,
    documentSize: (d.documentSize ?? d.DocumentSize ?? null) as number | null,
    documentType: (d.documentType ?? d.DocumentType ?? '') as string,
    canViewAsHtml: (d.canViewAsHtml ?? d.CanViewAsHtml ?? false) as boolean,
    canViewDirect: (d.canViewDirect ?? d.CanViewDirect ?? false) as boolean,
    fileExtension: (d.fileExtension ?? d.FileExtension ?? '') as string,
    contentUrl: (d.contentUrl ?? d.ContentUrl ?? null) as string | null,
    isImage: (d.isImage ?? d.IsImage ?? false) as boolean,
    isPdf: (d.isPdf ?? d.IsPdf ?? false) as boolean
  }
}

export async function getDealDocuments(dealKey: number): Promise<DealDocumentsResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/deals/${dealKey}/documents`)
  const docsRaw = (data.documents ?? data.Documents ?? []) as Record<string, unknown>[]
  return {
    documents: docsRaw.map(convertDealDocumentInfo)
  }
}

export async function getDocumentContent(documentKey: number): Promise<DocumentContentResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/documents/${documentKey}/content`)
  return {
    documentKey: (data.documentKey ?? data.DocumentKey ?? 0) as number,
    name: (data.name ?? data.Name ?? '') as string,
    html: (data.html ?? data.Html ?? null) as string | null,
    contentUrl: (data.contentUrl ?? data.ContentUrl ?? null) as string | null,
    contentType: (data.contentType ?? data.ContentType ?? null) as string | null,
    base64Content: (data.base64Content ?? data.Base64Content ?? null) as string | null
  }
}

// ============================================================================
// Document Upload
// ============================================================================

export async function getDocumentCategories(): Promise<DocumentCategoryInfo[]> {
  const { data } = await api().get<Record<string, unknown>>('/document-categories')
  const catsRaw = (data.categories ?? data.Categories ?? []) as Record<string, unknown>[]
  return catsRaw.map(c => ({
    categoryKey: (c.categoryKey ?? c.CategoryKey ?? 0) as number,
    name: (c.name ?? c.Name ?? '') as string,
    startingNumber: (c.startingNumber ?? c.StartingNumber ?? null) as number | null,
    endingNumber: (c.endingNumber ?? c.EndingNumber ?? null) as number | null,
    isGroup: (c.isGroup ?? c.IsGroup ?? false) as boolean
  }))
}

export async function uploadDealDocument(
  dealKey: number,
  file: File,
  categoryKey: number,
  name: string,
  description: string
): Promise<UploadDocumentResponse> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('categoryKey', categoryKey.toString())
  formData.append('name', name)
  formData.append('description', description)

  const { data } = await api().post<Record<string, unknown>>(
    `/deals/${dealKey}/documents/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
  )
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    documentKey: (data.documentKey ?? data.DocumentKey ?? null) as number | null
  }
}

// ============================================================================
// Advertiser Documents
// ============================================================================

function convertAdvertiserDocumentInfo(d: Record<string, unknown>): AdvertiserDocumentInfo {
  return {
    documentKey: (d.documentKey ?? d.DocumentKey ?? 0) as number,
    name: (d.name ?? d.Name ?? '') as string,
    description: (d.description ?? d.Description ?? '') as string,
    filename: (d.filename ?? d.Filename ?? '') as string,
    documentSize: (d.documentSize ?? d.DocumentSize ?? null) as number | null,
    documentType: (d.documentType ?? d.DocumentType ?? '') as string,
    canViewAsHtml: (d.canViewAsHtml ?? d.CanViewAsHtml ?? false) as boolean,
    canViewDirect: (d.canViewDirect ?? d.CanViewDirect ?? false) as boolean,
    fileExtension: (d.fileExtension ?? d.FileExtension ?? '') as string,
    dealKey: (d.dealKey ?? d.DealKey ?? null) as number | null,
    dealName: (d.dealName ?? d.DealName ?? '') as string,
    categoryName: (d.categoryName ?? d.CategoryName ?? '') as string,
    contentUrl: (d.contentUrl ?? d.ContentUrl ?? null) as string | null,
    isImage: (d.isImage ?? d.IsImage ?? false) as boolean,
    isPdf: (d.isPdf ?? d.IsPdf ?? false) as boolean
  }
}

export async function getAdvertiserDocuments(advertiserKey: number): Promise<AdvertiserDocumentInfo[]> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/documents`)
  const docsRaw = (data.documents ?? data.Documents ?? []) as Record<string, unknown>[]
  return docsRaw.map(convertAdvertiserDocumentInfo)
}

export async function uploadAdvertiserDocument(
  advertiserKey: number,
  file: File,
  categoryKey: number,
  name: string,
  description: string,
  dealKey?: number | null
): Promise<UploadDocumentResponse> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('categoryKey', categoryKey.toString())
  formData.append('name', name)
  formData.append('description', description)
  if (dealKey) {
    formData.append('dealKey', dealKey.toString())
  }

  const { data } = await api().post<Record<string, unknown>>(
    `/accounts/${advertiserKey}/documents/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
  )
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    documentKey: (data.documentKey ?? data.DocumentKey ?? null) as number | null
  }
}

// ============================================================================
// Global Search
// ============================================================================

const matchTypeLookup: Record<number, SearchMatchType> = {
  0: 'ContactName',
  1: 'ContactPhone',
  2: 'ContactEmail',
  3: 'AdvertiserPhone',
  4: 'AdvertiserEmail',
  5: 'AdvertiserName',
  6: 'AdvertiserAddressee'
}

export async function globalSearch(query: string, offset = 0): Promise<{ items: SearchResultItem[]; hasMore: boolean }> {
  const { data } = await api().get<Record<string, unknown>>('/search', { params: { q: query, offset } })
  const resultsRaw = (data.results ?? data.Results ?? []) as Record<string, unknown>[]
  const items = resultsRaw.map(r => ({
    advertiserKey: (r.advertiserKey ?? r.AdvertiserKey ?? 0) as number,
    advertiserName: (r.advertiserName ?? r.AdvertiserName ?? '') as string,
    contactKey: (r.contactKey ?? r.ContactKey ?? null) as number | null,
    matchType: matchTypeLookup[(r.matchType ?? r.MatchType ?? 0) as number] ?? 'ContactName',
    matchTypeDisplay: (r.matchTypeDisplay ?? r.MatchTypeDisplay ?? '') as string,
    matchedValue: (r.matchedValue ?? r.MatchedValue ?? '') as string,
    contactName: (r.contactName ?? r.ContactName ?? null) as string | null
  }))
  const hasMore = (data.hasMore ?? data.HasMore ?? false) as boolean
  return { items, hasMore }
}

// ── Payment Processing ─────────────────────────────────────────────────────

export async function getPaymentConfig(advertiserKey: number): Promise<PaymentConfig> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/paymentconfig`)
  return {
    isConfigured: (data.isConfigured ?? data.IsConfigured ?? false) as boolean,
    tokenizerUrl: (data.tokenizerUrl ?? data.TokenizerUrl ?? '') as string,
    publicKey: (data.publicKey ?? data.PublicKey ?? '') as string,
    useSandbox: (data.useSandbox ?? data.UseSandbox ?? false) as boolean,
    surchargingEnabled: (data.surchargingEnabled ?? data.SurchargingEnabled ?? false) as boolean,
    surchargePercentage: (data.surchargePercentage ?? data.SurchargePercentage ?? null) as number | null,
    surchargeLabel: (data.surchargeLabel ?? data.SurchargeLabel ?? 'Credit Card Surcharge') as string,
    achEnabled: (data.achEnabled ?? data.AchEnabled ?? false) as boolean
  }
}

function convertVaultedCardInfo(d: Record<string, unknown>): VaultedCardInfo {
  return {
    id: (d.id ?? d.Id ?? '') as string,
    cardType: (d.cardType ?? d.CardType ?? '') as string,
    lastFourDigits: (d.lastFourDigits ?? d.LastFourDigits ?? '') as string,
    expiration: (d.expiration ?? d.Expiration ?? '') as string,
    paymentMethodType: (d.paymentMethodType ?? d.PaymentMethodType ?? '') as string,
    isExpired: (d.isExpired ?? d.IsExpired ?? false) as boolean,
    displayText: (d.displayText ?? d.DisplayText ?? '') as string,
    surchargePercentage: (d.surchargePercentage ?? d.SurchargePercentage ?? null) as number | null
  }
}

function convertVaultedAddressInfo(d: Record<string, unknown>): VaultedAddressInfo {
  return {
    id: (d.id ?? d.Id ?? '') as string,
    addressLine1: (d.addressLine1 ?? d.AddressLine1 ?? '') as string,
    addressLine2: (d.addressLine2 ?? d.AddressLine2 ?? '') as string,
    city: (d.city ?? d.City ?? '') as string,
    state: (d.state ?? d.State ?? '') as string,
    postalCode: (d.postalCode ?? d.PostalCode ?? '') as string,
    country: (d.country ?? d.Country ?? '') as string,
    isDefault: (d.isDefault ?? d.IsDefault ?? false) as boolean
  }
}

export async function getVaultedCards(advertiserKey: number): Promise<VaultedCardsResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/vaultedcards`)
  const cardsRaw = (data.cards ?? data.Cards ?? []) as Record<string, unknown>[]
  const addressesRaw = (data.addresses ?? data.Addresses ?? []) as Record<string, unknown>[]
  return {
    cards: cardsRaw.map(convertVaultedCardInfo),
    addresses: addressesRaw.map(convertVaultedAddressInfo),
    defaultBillingAddressId: (data.defaultBillingAddressId ?? data.DefaultBillingAddressId ?? null) as string | null
  }
}

export async function processPayment(advertiserKey: number, request: ProcessPaymentRequest): Promise<ProcessPaymentResponse> {
  const { data } = await api().post<Record<string, unknown>>(`/accounts/${advertiserKey}/payment`, {
    Token: request.token,
    Amount: request.amount,
    SurchargeAmount: request.surchargeAmount,
    AllocationType: request.allocationType,
    InvoiceAllocations: request.invoiceAllocations.map(a => ({
      InvoiceKey: a.invoiceKey,
      Amount: a.amount
    })),
    BillingAddress: request.billingAddress ? {
      FirstName: request.billingAddress.firstName,
      LastName: request.billingAddress.lastName,
      Company: request.billingAddress.company,
      AddressLine1: request.billingAddress.addressLine1,
      AddressLine2: request.billingAddress.addressLine2,
      City: request.billingAddress.city,
      State: request.billingAddress.state,
      PostalCode: request.billingAddress.postalCode,
      Country: request.billingAddress.country
    } : null,
    SaveCard: request.saveCard,
    Memo: request.memo,
    PaymentMethodType: request.paymentMethodType,
    VaultedPaymentMethodId: request.vaultedPaymentMethodId
  }, { timeout: 120000 })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    ...readPaymentSafety(data),
    message: (data.message ?? data.Message ?? '') as string,
    transactionId: (data.transactionId ?? data.TransactionId ?? '') as string,
    authorizationCode: (data.authorizationCode ?? data.AuthorizationCode ?? '') as string,
    totalAmountCharged: (data.totalAmountCharged ?? data.TotalAmountCharged ?? 0) as number,
    actualSurchargeAmount: (data.actualSurchargeAmount ?? data.ActualSurchargeAmount ?? 0) as number,
    newAccountBalance: (data.newAccountBalance ?? data.NewAccountBalance ?? 0) as number,
    cardSaved: (data.cardSaved ?? data.CardSaved ?? false) as boolean,
    partialAccountNumber: (data.partialAccountNumber ?? data.PartialAccountNumber ?? '') as string,
    accountType: (data.accountType ?? data.AccountType ?? '') as string,
    paymentKey: (data.paymentKey ?? data.PaymentKey ?? null) as number | null
  }
}

export async function addVaultPaymentMethod(advertiserKey: number, request: AddVaultPaymentMethodRequest): Promise<AddVaultPaymentMethodResponse> {
  const { data } = await api().post<Record<string, unknown>>(`/accounts/${advertiserKey}/vault/add`, {
    Token: request.token,
    PaymentMethodType: request.paymentMethodType,
    BillingAddress: request.billingAddress ? {
      FirstName: request.billingAddress.firstName,
      LastName: request.billingAddress.lastName,
      Company: request.billingAddress.company,
      AddressLine1: request.billingAddress.addressLine1,
      AddressLine2: request.billingAddress.addressLine2,
      City: request.billingAddress.city,
      State: request.billingAddress.state,
      PostalCode: request.billingAddress.postalCode,
      Country: request.billingAddress.country
    } : null,
    ExistingAddressId: request.existingAddressId
  }, { timeout: 120000 })
  const newMethodRaw = (data.newPaymentMethod ?? data.NewPaymentMethod ?? null) as Record<string, unknown> | null
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    newPaymentMethod: newMethodRaw ? convertVaultedCardInfo(newMethodRaw) : null
  }
}

export async function deleteVaultPaymentMethod(advertiserKey: number, request: DeleteVaultPaymentMethodRequest): Promise<DeleteVaultPaymentMethodResponse> {
  const { data } = await api().post<Record<string, unknown>>(`/accounts/${advertiserKey}/vault/delete`, {
    PaymentMethodId: request.paymentMethodId,
    PaymentMethodType: request.paymentMethodType
  })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

export async function updateVaultExpiration(advertiserKey: number, request: UpdateVaultExpirationRequest): Promise<UpdateVaultExpirationResponse> {
  const { data } = await api().post<Record<string, unknown>>(`/accounts/${advertiserKey}/vault/update-expiration`, {
    PaymentMethodId: request.paymentMethodId,
    ExpirationDate: request.expirationDate
  })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

// ============================================================================
// Advertiser Edit
// ============================================================================

export async function getAdvertiserEditForm(advertiserKey: number): Promise<AdvertiserEditFormResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/accounts/${advertiserKey}/edit`)

  const rawData = (data.data ?? data.Data ?? {}) as Record<string, unknown>
  const rawPerms = (data.permissions ?? data.Permissions ?? {}) as Record<string, unknown>
  const rawSets = (data.advertiserSets ?? data.AdvertiserSets ?? []) as Record<string, unknown>[]
  const rawOffices = (data.billingOffices ?? data.BillingOffices ?? []) as Record<string, unknown>[]
  const rawReps = (data.salesReps ?? data.SalesReps ?? []) as Record<string, unknown>[]

  const editData: AdvertiserEditData = {
    name: (rawData.name ?? rawData.Name ?? '') as string,
    addressName: (rawData.addressName ?? rawData.AddressName ?? '') as string,
    emailAddress: (rawData.emailAddress ?? rawData.EmailAddress ?? '') as string,
    mainPhone: (rawData.mainPhone ?? rawData.MainPhone ?? '') as string,
    mainFax: (rawData.mainFax ?? rawData.MainFax ?? '') as string,
    webSite: (rawData.webSite ?? rawData.WebSite ?? '') as string,
    street1: (rawData.street1 ?? rawData.Street1 ?? '') as string,
    street2: (rawData.street2 ?? rawData.Street2 ?? '') as string,
    city: (rawData.city ?? rawData.City ?? '') as string,
    state: (rawData.state ?? rawData.State ?? '') as string,
    postalCode: (rawData.postalCode ?? rawData.PostalCode ?? '') as string,
    country: (rawData.country ?? rawData.Country ?? '') as string,
    advertiserSetFKey: (rawData.advertiserSetFKey ?? rawData.AdvertiserSetFKey ?? null) as number | null,
    billingOfficeFKey: (rawData.billingOfficeFKey ?? rawData.BillingOfficeFKey ?? null) as number | null,
    salesRepFKey: (rawData.salesRepFKey ?? rawData.SalesRepFKey ?? null) as number | null,
    inactive: (rawData.inactive ?? rawData.Inactive ?? false) as boolean
  }

  const permissions: AdvertiserEditPermissions = {
    canCreate: (rawPerms.canCreate ?? rawPerms.CanCreate ?? false) as boolean,
    canUpdate: (rawPerms.canUpdate ?? rawPerms.CanUpdate ?? false) as boolean,
    canEditAdvertiserSet: (rawPerms.canEditAdvertiserSet ?? rawPerms.CanEditAdvertiserSet ?? false) as boolean,
    canEditBillingOffice: (rawPerms.canEditBillingOffice ?? rawPerms.CanEditBillingOffice ?? false) as boolean,
    canEditSalesRep: (rawPerms.canEditSalesRep ?? rawPerms.CanEditSalesRep ?? false) as boolean,
    canEditInactiveStatus: (rawPerms.canEditInactiveStatus ?? rawPerms.CanEditInactiveStatus ?? false) as boolean
  }

  return {
    data: editData,
    permissions,
    advertiserSets: rawSets.map(convertDealLookupItem),
    billingOffices: rawOffices.map(convertDealLookupItem),
    salesReps: rawReps.map(convertDealLookupItem)
  }
}

export async function updateAdvertiser(advertiserKey: number, request: SaveAdvertiserRequest): Promise<SaveAdvertiserResponse> {
  const { data } = await api().put<Record<string, unknown>>(`/accounts/${advertiserKey}`, {
    Name: request.name,
    AddressName: request.addressName,
    EmailAddress: request.emailAddress,
    MainPhone: request.mainPhone,
    MainFax: request.mainFax,
    WebSite: request.webSite,
    Street1: request.street1,
    Street2: request.street2,
    City: request.city,
    State: request.state,
    PostalCode: request.postalCode,
    Country: request.country,
    AdvertiserSetFKey: request.advertiserSetFKey,
    BillingOfficeFKey: request.billingOfficeFKey,
    SalesRepFKey: request.salesRepFKey,
    Inactive: request.inactive
  })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string
  }
}

export async function getNewAdvertiserForm(): Promise<AdvertiserEditFormResponse> {
  const { data } = await api().get<Record<string, unknown>>('/accounts/new')

  const rawData = (data.data ?? data.Data ?? {}) as Record<string, unknown>
  const rawPerms = (data.permissions ?? data.Permissions ?? {}) as Record<string, unknown>
  const rawSets = (data.advertiserSets ?? data.AdvertiserSets ?? []) as Record<string, unknown>[]
  const rawOffices = (data.billingOffices ?? data.BillingOffices ?? []) as Record<string, unknown>[]
  const rawReps = (data.salesReps ?? data.SalesReps ?? []) as Record<string, unknown>[]

  const editData: AdvertiserEditData = {
    name: (rawData.name ?? rawData.Name ?? '') as string,
    addressName: (rawData.addressName ?? rawData.AddressName ?? '') as string,
    emailAddress: (rawData.emailAddress ?? rawData.EmailAddress ?? '') as string,
    mainPhone: (rawData.mainPhone ?? rawData.MainPhone ?? '') as string,
    mainFax: (rawData.mainFax ?? rawData.MainFax ?? '') as string,
    webSite: (rawData.webSite ?? rawData.WebSite ?? '') as string,
    street1: (rawData.street1 ?? rawData.Street1 ?? '') as string,
    street2: (rawData.street2 ?? rawData.Street2 ?? '') as string,
    city: (rawData.city ?? rawData.City ?? '') as string,
    state: (rawData.state ?? rawData.State ?? '') as string,
    postalCode: (rawData.postalCode ?? rawData.PostalCode ?? '') as string,
    country: (rawData.country ?? rawData.Country ?? '') as string,
    advertiserSetFKey: (rawData.advertiserSetFKey ?? rawData.AdvertiserSetFKey ?? null) as number | null,
    billingOfficeFKey: (rawData.billingOfficeFKey ?? rawData.BillingOfficeFKey ?? null) as number | null,
    salesRepFKey: (rawData.salesRepFKey ?? rawData.SalesRepFKey ?? null) as number | null,
    inactive: (rawData.inactive ?? rawData.Inactive ?? false) as boolean
  }

  const permissions: AdvertiserEditPermissions = {
    canCreate: (rawPerms.canCreate ?? rawPerms.CanCreate ?? false) as boolean,
    canUpdate: (rawPerms.canUpdate ?? rawPerms.CanUpdate ?? false) as boolean,
    canEditAdvertiserSet: (rawPerms.canEditAdvertiserSet ?? rawPerms.CanEditAdvertiserSet ?? false) as boolean,
    canEditBillingOffice: (rawPerms.canEditBillingOffice ?? rawPerms.CanEditBillingOffice ?? false) as boolean,
    canEditSalesRep: (rawPerms.canEditSalesRep ?? rawPerms.CanEditSalesRep ?? false) as boolean,
    canEditInactiveStatus: (rawPerms.canEditInactiveStatus ?? rawPerms.CanEditInactiveStatus ?? false) as boolean
  }

  return {
    data: editData,
    permissions,
    advertiserSets: rawSets.map(convertDealLookupItem),
    billingOffices: rawOffices.map(convertDealLookupItem),
    salesReps: rawReps.map(convertDealLookupItem)
  }
}

export async function createAdvertiser(request: SaveAdvertiserRequest): Promise<CreateAdvertiserResponse> {
  const { data } = await api().post<Record<string, unknown>>('/accounts', {
    Name: request.name,
    AddressName: request.addressName,
    EmailAddress: request.emailAddress,
    MainPhone: request.mainPhone,
    MainFax: request.mainFax,
    WebSite: request.webSite,
    Street1: request.street1,
    Street2: request.street2,
    City: request.city,
    State: request.state,
    PostalCode: request.postalCode,
    Country: request.country,
    AdvertiserSetFKey: request.advertiserSetFKey,
    BillingOfficeFKey: request.billingOfficeFKey,
    SalesRepFKey: request.salesRepFKey,
    Inactive: request.inactive
  })
  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: (data.message ?? data.Message ?? '') as string,
    advertiserKey: (data.advertiserKey ?? data.AdvertiserKey ?? 0) as number
  }
}

// ============================================================================
// Order Creation
// ============================================================================

function convertOrderLookupItem(d: Record<string, unknown>): { key: number; name: string } {
  return {
    key: (d.key ?? d.Key ?? 0) as number,
    name: ((d.name ?? d.Name ?? '') as string)
  }
}

export async function getCreateOrderLookups(advertiserKey: number): Promise<CreateOrderLookupsResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/orders/create-lookups?advertiserKey=${advertiserKey}`)

  const openRateCardsRaw = (data.openRateCards ?? data.OpenRateCards ?? []) as Record<string, unknown>[]
  const agreementsRaw = (data.agreements ?? data.Agreements ?? []) as Record<string, unknown>[]
  const salesTypesRaw = (data.salesTypes ?? data.SalesTypes ?? []) as Record<string, unknown>[]
  const sectionsRaw = (data.sections ?? data.Sections ?? []) as Record<string, unknown>[]
  const publicationsRaw = (data.publications ?? data.Publications ?? []) as Record<string, unknown>[]
  const featuresRaw = (data.features ?? data.Features ?? []) as Record<string, unknown>[]
  const discountsRaw = (data.discounts ?? data.Discounts ?? []) as Record<string, unknown>[]
  const contactsRaw = (data.contacts ?? data.Contacts ?? []) as Record<string, unknown>[]
  const holdReasonsRaw = (data.holdReasons ?? data.HoldReasons ?? []) as Record<string, unknown>[]
  const pricingAdjReasonsRaw = (data.pricingAdjustmentReasons ?? data.PricingAdjustmentReasons ?? []) as Record<string, unknown>[]

  return {
    openRateCards: openRateCardsRaw.map(d => ({
      key: (d.key ?? d.Key ?? 0) as number,
      name: ((d.name ?? d.Name ?? '') as string)
    })),
    agreements: agreementsRaw.map(d => ({
      key: (d.key ?? d.Key ?? 0) as number,
      name: ((d.name ?? d.Name ?? '') as string),
      contractRateCardFKey: (d.contractRateCardFKey ?? d.ContractRateCardFKey ?? null) as number | null,
      issueStartDate: (d.issueStartDate ?? d.IssueStartDate ?? null) as string | null,
      issueEndDate: (d.issueEndDate ?? d.IssueEndDate ?? null) as string | null
    })),
    salesTypes: salesTypesRaw.map(convertOrderLookupItem),
    sections: sectionsRaw.map(convertOrderLookupItem),
    publications: publicationsRaw.map(d => ({
      publicationKey: (d.publicationKey ?? d.PublicationKey ?? 0) as number,
      name: ((d.name ?? d.Name ?? '') as string),
      abbreviation: ((d.abbreviation ?? d.Abbreviation ?? '') as string)
    })),
    features: featuresRaw.map(convertOrderLookupItem),
    discounts: discountsRaw.map(convertOrderLookupItem),
    contacts: contactsRaw.map(convertOrderLookupItem),
    creditLimit: (data.creditLimit ?? data.CreditLimit ?? 0) as number,
    creditExtended: (data.creditExtended ?? data.CreditExtended ?? 0) as number,
    hasAgreements: (data.hasAgreements ?? data.HasAgreements ?? false) as boolean,
    holdReasons: holdReasonsRaw.map(convertReasonInfo),
    pricingAdjustmentReasons: pricingAdjReasonsRaw.map(d => ({
      key: (d.key ?? d.Key ?? 0) as number,
      name: ((d.name ?? d.Name ?? '') as string),
      isDiscount: (d.isDiscount ?? d.IsDiscount ?? false) as boolean
    }))
  }
}

export async function getRateCardDetail(rateCardKey: number, rateCardType: string): Promise<RateCardDetailResponse> {
  const { data } = await api().get<Record<string, unknown>>(`/orders/rate-card-detail?rateCardKey=${rateCardKey}&rateCardType=${rateCardType}`)
  console.log('[api] getRateCardDetail raw response:', JSON.stringify(data).substring(0, 500))

  const coveredRatesRaw = (data.coveredRates ?? data.CoveredRates ?? []) as Record<string, unknown>[]
  const publicationsRaw = (data.publications ?? data.Publications ?? []) as Record<string, unknown>[]

  return {
    rateCardKey: (data.rateCardKey ?? data.RateCardKey ?? 0) as number,
    name: ((data.name ?? data.Name ?? '') as string),
    coveredRates: coveredRatesRaw.map(d => ({
      key: (d.key ?? d.Key ?? 0) as number,
      name: ((d.name ?? d.Name ?? '') as string),
      defaultAdWidth: (d.defaultAdWidth ?? d.DefaultAdWidth ?? null) as number | null,
      defaultAdHeight: (d.defaultAdHeight ?? d.DefaultAdHeight ?? null) as number | null,
      defaultPricingType: ((d.defaultPricingType ?? d.DefaultPricingType ?? '') as string)
    })),
    publications: publicationsRaw.map(d => ({
      publicationKey: (d.publicationKey ?? d.PublicationKey ?? 0) as number,
      name: ((d.name ?? d.Name ?? '') as string),
      abbreviation: ((d.abbreviation ?? d.Abbreviation ?? '') as string),
      hasRegularRate: (d.hasRegularRate ?? d.HasRegularRate ?? false) as boolean,
      hasPickupRate: (d.hasPickupRate ?? d.HasPickupRate ?? false) as boolean,
      regularUnitRate: (d.regularUnitRate ?? d.RegularUnitRate ?? null) as number | null,
      pickupUnitRate: (d.pickupUnitRate ?? d.PickupUnitRate ?? null) as number | null,
      publishedOnMonday: (d.publishedOnMonday ?? d.PublishedOnMonday ?? false) as boolean,
      publishedOnTuesday: (d.publishedOnTuesday ?? d.PublishedOnTuesday ?? false) as boolean,
      publishedOnWednesday: (d.publishedOnWednesday ?? d.PublishedOnWednesday ?? false) as boolean,
      publishedOnThursday: (d.publishedOnThursday ?? d.PublishedOnThursday ?? false) as boolean,
      publishedOnFriday: (d.publishedOnFriday ?? d.PublishedOnFriday ?? false) as boolean,
      publishedOnSaturday: (d.publishedOnSaturday ?? d.PublishedOnSaturday ?? false) as boolean,
      publishedOnSunday: (d.publishedOnSunday ?? d.PublishedOnSunday ?? false) as boolean
    })),
    availableSalesTypeKeys: (data.availableSalesTypeKeys ?? data.AvailableSalesTypeKeys ?? null) as number[] | null,
    availableSectionKeys: (data.availableSectionKeys ?? data.AvailableSectionKeys ?? null) as number[] | null,
    availableFeatureKeys: (data.availableFeatureKeys ?? data.AvailableFeatureKeys ?? null) as number[] | null,
    availableDiscountKeys: (data.availableDiscountKeys ?? data.AvailableDiscountKeys ?? null) as number[] | null
  }
}

export async function getSchedulePreview(request: SchedulePreviewRequest): Promise<SchedulePreviewResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/schedule-preview', {
    PublicationKeys: request.publicationKeys,
    PickupPublicationKeys: request.pickupPublicationKeys,
    SchedulingMethod: request.schedulingMethod,
    StartDate: request.startDate,
    EndDate: request.endDate,
    NumberOfTimes: request.numberOfTimes,
    NumberOfTimesMethod: request.numberOfTimesMethod,
    RunFrequency: request.runFrequency,
    DaysOfWeek: request.daysOfWeek,
    StartingImageNumber: request.startingImageNumber,
    ImageChangeMode: request.imageChangeMode,
    AdvertiserFKey: request.advertiserFKey,
    PricingContextType: request.pricingContextType,
    OpenRateCardFKey: request.openRateCardFKey,
    ContractRateCardFKey: request.contractRateCardFKey,
    AgreementFKey: request.agreementFKey,
    RetailRateFKey: request.retailRateFKey,
    SalesTypeFKey: request.salesTypeFKey,
    SectionFKey: request.sectionFKey,
    Color: request.color,
    AdWidth: request.adWidth,
    AdHeight: request.adHeight,
    FeatureKeys: request.featureKeys,
    DiscountKeys: request.discountKeys
  })

  const runsRaw = (data.runs ?? data.Runs ?? []) as Record<string, unknown>[]
  return {
    runs: runsRaw.map(d => ({
      date: ((d.date ?? d.Date ?? '') as string),
      publicationFKey: (d.publicationFKey ?? d.PublicationFKey ?? 0) as number,
      publicationName: ((d.publicationName ?? d.PublicationName ?? '') as string),
      publicationAbbreviation: ((d.publicationAbbreviation ?? d.PublicationAbbreviation ?? '') as string),
      imageNumber: (d.imageNumber ?? d.ImageNumber ?? 1) as number,
      estimatedBasePrice: (d.estimatedBasePrice ?? d.EstimatedBasePrice ?? 0) as number,
      estimatedFeaturePrice: (d.estimatedFeaturePrice ?? d.EstimatedFeaturePrice ?? 0) as number,
      estimatedDiscountAmount: (d.estimatedDiscountAmount ?? d.EstimatedDiscountAmount ?? 0) as number,
      estimatedSalesTax: (d.estimatedSalesTax ?? d.EstimatedSalesTax ?? 0) as number,
      estimatedTotal: (d.estimatedTotal ?? d.EstimatedTotal ?? 0) as number
    })),
    totalRuns: (data.totalRuns ?? data.TotalRuns ?? 0) as number,
    publicationIssueInfo: ((data.publicationIssueInfo ?? data.PublicationIssueInfo ?? []) as Record<string, unknown>[]).map(d => ({
      publicationFKey: (d.publicationFKey ?? d.PublicationFKey ?? 0) as number,
      publicationName: ((d.publicationName ?? d.PublicationName ?? '') as string),
      firstOpenIssueDate: ((d.firstOpenIssueDate ?? d.FirstOpenIssueDate ?? null) as string | null)
    }))
  }
}

export async function getPublicationCalendar(request: PublicationCalendarRequest): Promise<PublicationCalendarResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/publication-calendar', {
    PublicationKeys: request.publicationKeys,
    PickupPublicationKeys: request.pickupPublicationKeys,
    EarliestDate: request.earliestDate,
    LatestDate: request.latestDate
  })

  return {
    dates: ((data.dates ?? data.Dates ?? []) as Record<string, unknown>[]).map(d => ({
      date: ((d.date ?? d.Date ?? '') as string),
      publicationCount: (d.publicationCount ?? d.PublicationCount ?? 0) as number,
      publicationNames: ((d.publicationNames ?? d.PublicationNames ?? []) as string[])
    }))
  }
}

export async function createRetailOrder(request: CreateOrderRequest): Promise<CreateOrderResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/create', {
    AdvertiserFKey: request.advertiserFKey,
    ContactFKey: request.contactFKey,
    OrderDate: request.orderDate,
    Description: request.description,
    AdvertiserReferenceNumber: request.advertiserReferenceNumber,
    PricingContextType: request.pricingContextType,
    OpenRateCardFKey: request.openRateCardFKey,
    AgreementFKey: request.agreementFKey,
    ContractRateCardFKey: request.contractRateCardFKey,
    RetailRateFKey: request.retailRateFKey,
    SalesTypeFKey: request.salesTypeFKey,
    SectionFKey: request.sectionFKey,
    ColumnRateAdWidth: request.columnRateAdWidth,
    ColumnRateAdHeight: request.columnRateAdHeight,
    Color: request.color,
    PublicationKeys: request.publicationKeys,
    PickupPublicationKeys: request.pickupPublicationKeys,
    FeatureKeys: request.featureKeys,
    DiscountKeys: request.discountKeys,
    PagePosition: request.pagePosition,
    IsGuaranteedPlacement: request.isGuaranteedPlacement,
    IsCoupon: request.isCoupon,
    IsReverse: request.isReverse,
    ManifestNotes: request.manifestNotes,
    RequestedPageNumber: request.requestedPageNumber,
    SchedulingMethod: request.schedulingMethod,
    StartDate: request.startDate,
    EndDate: request.endDate,
    NumberOfTimes: request.numberOfTimes,
    NumberOfTimesMethod: request.numberOfTimesMethod,
    RunFrequency: request.runFrequency,
    DaysOfWeek: request.daysOfWeek
  }, { timeout: 120000 })

  const orderDetailRaw = (data.orderDetail ?? data.OrderDetail ?? null) as Record<string, unknown> | null

  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: ((data.message ?? data.Message ?? '') as string),
    orderKey: (data.orderKey ?? data.OrderKey ?? null) as number | null,
    orderNumber: ((data.orderNumber ?? data.OrderNumber ?? '') as string),
    orderDetail: orderDetailRaw ? convertRetailOrderDetail(orderDetailRaw) : null,
    creditLimitExceeded: (data.creditLimitExceeded ?? data.CreditLimitExceeded ?? false) as boolean
  }
}

export async function createOrderFromRuns(request: CreateOrderFromRunsRequest): Promise<CreateOrderResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/create-from-runs', {
    AdvertiserFKey: request.advertiserFKey,
    ContactFKey: request.contactFKey,
    OrderDate: request.orderDate,
    Description: request.description,
    AdvertiserReferenceNumber: request.advertiserReferenceNumber,
    PagePosition: request.pagePosition,
    IsGuaranteedPlacement: request.isGuaranteedPlacement,
    IsCoupon: request.isCoupon,
    IsReverse: request.isReverse,
    ManifestNotes: request.manifestNotes,
    RequestedPageNumber: request.requestedPageNumber,
    QuoteExpirationDate: request.quoteExpirationDate,
    Runs: request.runs.map(r => ({
      Date: r.date,
      PublicationFKey: r.publicationFKey,
      PublicationName: r.publicationName,
      PublicationAbbreviation: r.publicationAbbreviation,
      ImageNumber: r.imageNumber,
      EstimatedBasePrice: r.estimatedBasePrice,
      EstimatedFeaturePrice: r.estimatedFeaturePrice,
      EstimatedDiscountAmount: r.estimatedDiscountAmount,
      EstimatedSalesTax: r.estimatedSalesTax,
      EstimatedTotal: r.estimatedTotal,
      PricingContextType: r.pricingContextType,
      OpenRateCardFKey: r.openRateCardFKey,
      ContractRateCardFKey: r.contractRateCardFKey,
      AgreementFKey: r.agreementFKey,
      RetailRateFKey: r.retailRateFKey,
      SalesTypeFKey: r.salesTypeFKey,
      SectionFKey: r.sectionFKey,
      Color: r.color,
      AdWidth: r.adWidth,
      AdHeight: r.adHeight,
      FeatureKeys: r.featureKeys,
      DiscountKeys: r.discountKeys,
      IsQuote: r.isQuote,
      IsOnHold: r.isOnHold,
      HoldReasonFKey: r.holdReasonFKey,
      Description: r.description,
      SectionName: r.sectionName,
      FeatureNames: r.featureNames,
      DiscountNames: r.discountNames,
      RateCardName: r.rateCardName,
      SalesTypeName: r.salesTypeName,
      PricingAdjustment: r.pricingAdjustment,
      PricingAdjustmentReasonFKey: r.pricingAdjustmentReasonFKey
    }))
  }, { timeout: 120000 })

  const orderDetailRaw = (data.orderDetail ?? data.OrderDetail ?? null) as Record<string, unknown> | null

  return {
    success: (data.success ?? data.Success ?? false) as boolean,
    message: ((data.message ?? data.Message ?? '') as string),
    orderKey: (data.orderKey ?? data.OrderKey ?? null) as number | null,
    orderNumber: ((data.orderNumber ?? data.OrderNumber ?? '') as string),
    orderDetail: orderDetailRaw ? convertRetailOrderDetail(orderDetailRaw) : null,
    creditLimitExceeded: (data.creditLimitExceeded ?? data.CreditLimitExceeded ?? false) as boolean
  }
}

export async function getPublicationFilteredItems(publicationKeys: number[]): Promise<PublicationFilteredItemsResponse> {
  const { data } = await api().post<Record<string, unknown>>('/orders/publication-filtered-items', publicationKeys)
  return {
    featureKeys: (data.featureKeys ?? data.FeatureKeys ?? []) as number[],
    discountKeys: (data.discountKeys ?? data.DiscountKeys ?? []) as number[]
  }
}


/** Converts the Snapshot DTO property names using the API's established camel/Pascal convention. */
function snapshotProperties(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snapshotProperties)
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key[0].toLowerCase() + key.slice(1), snapshotProperties(item)]))
  return value
}
/** Reads independently recoverable Snapshot panels for an authenticated account. */
export async function getAccountSnapshot(advertiserKey: number, signal?: AbortSignal): Promise<AccountSnapshot> {
  const { data } = await api().get(`/accounts/${advertiserKey}/snapshot`, { signal })
  return snapshotProperties(data) as AccountSnapshot
}
/** Reads the exact selected chart population or next scheduled day. */
export async function getSnapshotRuns(advertiserKey: number, selection: SnapshotSelection, asOfDate: string, signal?: AbortSignal): Promise<SnapshotRunDetails> {
  const { data } = await api().get(`/accounts/${advertiserKey}/snapshot/${selection.kind === 'next' ? 'next-runs' : 'chart-runs'}`, {
    signal, params: { asOfDate: asOfDate.slice(0, 10), ...(selection.kind === 'chart' ? { monthStart: selection.monthStart?.slice(0, 10), segment: selection.segment } : {}) }
  })
  return snapshotProperties(data) as SnapshotRunDetails
}
/** Loads only the server-selected artwork for an exact saved placement. */
export async function getSnapshotArtwork(advertiserKey: number, runKey: number, historical: boolean, large: boolean, signal?: AbortSignal): Promise<SnapshotArtwork> {
  const { data } = await api().get(`/accounts/${advertiserKey}/snapshot/artwork/${runKey}`, { signal, params: { historical, large } })
  return snapshotProperties(data) as SnapshotArtwork
}
/** Opens an account activity without limiting it to the paged worklist's date range. */
export async function getSnapshotActivity(advertiserKey: number, activityKey: number): Promise<ActivityInfo> {
  const { data } = await api().get(`/accounts/${advertiserKey}/activities/${activityKey}`)
  return convertActivityInfo(data)
}

/** Reads current type assignments for one account. */
export async function getAccountTypes(advertiserKey: number): Promise<import('@/types/accountTypes').AccountTypesResponse> {
  const { data } = await api().get(`/accounts/${advertiserKey}/types`)
  const types = data.types ?? data.Types
  const keys = data.assignedTypeKeys ?? data.AssignedTypeKeys
  if (!Array.isArray(types) || !Array.isArray(keys)) throw new Error('Types could not be verified. Please retry.')
  return {
    types: types.map((item: Record<string, unknown>) => ({
      key: Number(item.key ?? item.Key),
      name: String(item.name ?? item.Name ?? ''),
      inactive: Boolean(item.inactive ?? item.Inactive)
    })),
    assignedTypeKeys: keys.map(Number),
    canUpdate: (data.canUpdate ?? data.CanUpdate) === true
  }
}

/** Sets one membership explicitly so retries never toggle a successful prior save. */
export async function setAccountType(advertiserKey: number, typeKey: number, assigned: boolean): Promise<import('@/types/accountTypes').SetAccountTypeResponse> {
  const { data } = await api().put(`/accounts/${advertiserKey}/types/${typeKey}`, { assigned })
  const confirmed = data.assigned ?? data.Assigned
  return {
    success: (data.success ?? data.Success) === true,
    message: String(data.message ?? data.Message ?? ''),
    assigned: typeof confirmed === 'boolean' ? confirmed : null
  }
}
